import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfirm } from '../contexts/ConfirmContext';
import { MarketBotAdminPanel } from './MarketBotAdminPanel';
import { BotActivityFeed } from './BotActivityFeed';
import { PlayerWealthMonitor } from './PlayerWealthMonitor';
import { AdminQuickActions } from './AdminQuickActions';
import { AdvancedSearch } from './AdvancedSearch';
import { TerritoryOwnershipSummary } from './TerritoryOwnershipSummary';
import { TerritoryFixerPanel } from './TerritoryFixerPanel';
import { AdminAuditTrail } from './AdminAuditTrail';
import { AdminAlertSystem } from './AdminAlertSystem';
import { EconomyChartsAndTrends } from './EconomyChartsAndTrends';
import { BotOrdersDisplay } from './BotOrdersDisplay';
import { MailComposer } from './MailComposer';
import { RiftManagementPanel } from './RiftManagementPanel';

interface ProductionAdminPanelProps {
  apiBaseUrl: string;
}

interface CollisionDebugData {
  collisionCacheSize: number;
  totalIslands: number;
  permanentIslands: number;
  proceduralIslands: number;
  sampleTiles: Array<{ tile: string; isBlocked: boolean }>;
  status?: string;
  message?: string;
}

interface AdminActionState {
  isBusy: boolean;
  lastMessage: string | null;
  messageType: 'success' | 'error' | 'info';
}

interface ServerStats {
  connectedPlayers: number;
  totalShips: number;
  worldSeed: number;
  uptime: string;
}

interface EconomyStats {
  timestamp: number;
  currency: {
    totalPlayerPieces: number;
    totalNpcPieces: number;
    totalBounties: number;
    avgPlayerPieces: number;
    totalInCirculation: number;
  };
  players: {
    online: number;
    avgLevel: number;
    levelDistribution: Record<string, number>;
    wealthRanking: Array<{ name: string; pieces: number; bounty: number; level: number }>;
  };
  npcs: {
    total: number;
    byFaction: { navy: number; pirate: number; bandit: number };
  };
  inventory: {
    totalItems: number;
    uniqueItemTypes: number;
    totalPlayers: number;
    topItems: Array<{ itemId: string; name: string; count: number }>;
  };
}

interface HistoricalDataPoint {
  timestamp: number;
  value: number;
}

interface WorldHistoryCharacter {
  id: string;
  name: string;
  level: number;
  reputation: number;
  berries: number;
  shipType: string;
  characterType: string;
  lastPlayedAt: string;
  isOnline: boolean;
  infamy?: number;
  accountId?: string;
}

interface AdminAccountSummary {
  id: string;
  email: string;
  displayName: string | null;
  username: string | null;
  status: 'user' | 'moderator' | 'admin';
}

interface AdminCharacterSummary {
  id: string;
  accountId: string;
  name: string;
  characterType: string;
  shipType: string;
  berries: number;
  level: number;
  reputation: number;
  infamy: number;
  lastPlayedAt: string;
  factionReputation?: {
    navy: number;
    pirate: number;
    merchant: number;
    smuggler?: number;
    scholars?: number;
    inquisition?: number;
  };
}

const StatLine: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <div style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
    <span>{label}</span>
    <span style={{ color: color ?? '#e2e8f0', fontWeight: 600 }}>{value}</span>
  </div>
);

interface FactionStats {
  factions: Array<{
    id: string;
    name: string;
    power: number;
    influence: number;
    territories: number;
    members: number;
    treasury: number;
  }>;
  territories: {
    total: number;
    controlled: Record<string, number>;
    contested: number;
  };
  conflicts: {
    active: number;
    resolved: number;
  };
  recentEvents: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: number;
    impact: string;
  }>;
}

interface NPCStats {
  total: number;
  byCategory: Record<string, number>;
  traders: number;
  routes: number;
  tradeRoutes: Array<{
    id: string;
    name: string;
    waypointCount: number;
    type: string;
    dangerLevel: number;
  }>;
}

interface WorldOverviewData {
  islands: Array<{
    id: string;
    x: number;
    y: number;
    radius: number;
    biome: string;
    shape: Array<{ x: number; y: number }>;
    isPermanent: boolean;
    permanentName: string | null;
    ownerId: string | null;
    ownerType?: string | null;
    ownerName: string | null;
    ownerShipColor?: string | null;
    customName?: string | null;
    claimRadius?: number | null;
  }>;
  resourceNodes?: Array<{
    id: string;
    type: string;
    x: number;
    y: number;
  }>;
  whirlpools: Array<{
    id: string;
    x: number;
    y: number;
    class: string;
    status: string;
    expiresAt: number;
  }>;

  riftStorms?: Array<{
    id: string;
    x: number;
    y: number;
    radius: number;
    tier: string;
    color: string;
    expiresAt: number;
  }>;
  combatHotspots: Array<{
    x: number;
    y: number;
  }>;
  players: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    level: number;
  }>;
  npcs: Array<{
    id: string;
    name: string;
    x: number;
    y: number;
    faction: string;
  }>;
  totalIslands: number;
  totalWhirlpools: number;
  totalCombatHotspots: number;
  totalPlayers: number;
  totalNpcs: number;
  totalResourceNodes?: number;
}

interface AdminMapFilters {
  showIslands: boolean;
  showPermanentTradingPosts: boolean;
  showPermanentShipyards: boolean;
  showPermanentTaverns: boolean;
  showPermanentHarborOffices: boolean;
  showPermanentOther: boolean;
  showClaimedIslands: boolean;
  showUnclaimedIslands: boolean;
  showPlayerTerritory: boolean;
  showRegimeTerritory: boolean;
  showWhirlpools: boolean;
  showCombatHotspots: boolean;
  showPlayers: boolean;
  showPlayerNames: boolean;
  showNpcs: boolean;
  showRiftStorms: boolean;
  enabledResourceNodeTypes: Record<string, boolean>;
}

interface AdminTerritoryIsland {
  id: string;
  name: string;
  customName?: string | null;
  ownerId: string | null;
  ownerType: string | null;
  ownerName: string | null;
  ownerShipColor: string | null;
  claimRadius: number | null;
  position: { x: number; y: number };
  type: string;
  biome: string;
  isPermanent: boolean;
}

interface GuestStats {
  windowDays: number;
  totalGuestSessions: number;
  totalUniqueGuests: number;
  daily: Array<{ date: string; uniqueGuests: number; sessions: number }>;
}

const WORLD_PIXELS = 8000; // 8x8 chunks × 1000px
const TILE_SIZE = 16;

// 1 world tile = 1 canvas pixel
const WORLD_TILES = WORLD_PIXELS / TILE_SIZE; // 500
const MAP_CANVAS_SIZE = WORLD_TILES;

// Display scale (CSS). Keep canvas pixel-perfect.
const MAP_DISPLAY_SIZE = 600;

const SCALE = MAP_CANVAS_SIZE / WORLD_PIXELS;

// Biome color palettes matching WorldMapScene
const BIOME_PALETTE: Record<string, { shore: string; main: string; accent: string }> = {
  beach:    { shore: '#f5deb3', main: '#c2b280', accent: '#d4c896' },
  forest:   { shore: '#c2b280', main: '#228b22', accent: '#2e8b2e' },
  mountain: { shore: '#8b8b83', main: '#696969', accent: '#808080' },
  jungle:   { shore: '#c2b280', main: '#006400', accent: '#228b22' },
  desert:   { shore: '#f4a460', main: '#daa520', accent: '#cd853f' },
  volcanic: { shore: '#2f2f2f', main: '#1a1a1a', accent: '#3d3d3d' },
  winter:   { shore: '#e0e0e0', main: '#fffafa', accent: '#f0f8ff' },
  mixed:    { shore: '#c2b280', main: '#4a7c23', accent: '#5a8c33' },
};

// Region definitions matching WorldMapScene
// Layout: '+' pattern for cardinal Reaches, corners have unique biomes, Arcadia at center
const WORLD_REGIONS = [
  // Cardinal directions ('+' pattern)
  { name: 'NORTHERN REACH', minX: 3000, maxX: 5000, minY: 0, maxY: 3000, color: '#1abc9c' },
  { name: 'SOUTHERN REACH', minX: 3000, maxX: 5000, minY: 5000, maxY: 8000, color: '#e67e22' },
  { name: 'EASTERN REACH', minX: 5000, maxX: 8000, minY: 3000, maxY: 5000, color: '#3498db' },
  { name: 'WESTERN REACH', minX: 0, maxX: 3000, minY: 3000, maxY: 5000, color: '#9b59b6' },
  // Corner regions
  { name: 'FROSTWIND ISLES', minX: 0, maxX: 3000, minY: 0, maxY: 3000, color: '#a8d8ea' },
  { name: 'SUNFIRE ARCHIPELAGO', minX: 5000, maxX: 8000, minY: 0, maxY: 3000, color: '#ff6b6b' },
  { name: 'STORMVEIL DEPTHS', minX: 0, maxX: 3000, minY: 5000, maxY: 8000, color: '#4a4e69' },
  { name: 'GOLDEN SHOALS', minX: 5000, maxX: 8000, minY: 5000, maxY: 8000, color: '#ffd93d' },
  // Center
  { name: 'ARCADIA', minX: 3000, maxX: 5000, minY: 3000, maxY: 5000, color: '#e74c3c' },
];

interface MiniWorldMapProps {
  apiBaseUrl: string;
  adminKey: string;
  onTeleport?: (worldX: number, worldY: number) => void;
  selectedPlayerId?: string;
  filters: AdminMapFilters;
  onResourceNodeTypesDiscovered?: (types: string[]) => void;
}

const MiniWorldMap: React.FC<MiniWorldMapProps> = ({ apiBaseUrl, adminKey, onTeleport, selectedPlayerId, filters, onResourceNodeTypesDiscovered }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mapData, setMapData] = useState<WorldOverviewData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; worldX: number; worldY: number } | null>(null);
  const [isRealtime, setIsRealtime] = useState<boolean>(false);
  const [animTick, setAnimTick] = useState<number>(0);

  const getPermanentTypeKey = (island: WorldOverviewData['islands'][number]): string => {
    const islandIdLower: string = island.id.toLowerCase();
    const nameLower: string = (island.permanentName || '').toLowerCase();
    if (islandIdLower.includes('trading_post') || nameLower.includes('trading post')) return 'trading_post';
    if (islandIdLower.includes('shipyard') || nameLower.includes('shipyard')) return 'shipyard';
    if (islandIdLower.includes('tavern') || nameLower.includes('tavern')) return 'tavern';
    if (islandIdLower.includes('harbor_office') || nameLower.includes('harbor office') || nameLower.includes('harbor master') || nameLower.includes('port authority') || nameLower.includes('fleet command')) return 'harbor_office';
    return 'other';
  };

  const pointInPolygon = (x: number, y: number, polygon: Array<{ x: number; y: number }>): boolean => {
    // Ray-casting algorithm
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;

      const intersects = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-9) + xi);
      if (intersects) inside = !inside;
    }
    return inside;
  };

  const fetchMapData = useCallback(async (): Promise<void> => {
    if (!apiBaseUrl || !adminKey) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/world-overview`, {
        headers: {
          'x-admin-key': adminKey,
        },
      });
      if (response.ok) {
        const data: WorldOverviewData = await response.json();
        setMapData(data);
        setLastUpdate(new Date().toLocaleTimeString());

        const nodes = Array.isArray(data.resourceNodes) ? data.resourceNodes : [];
        if (nodes.length > 0 && onResourceNodeTypesDiscovered) {
          const types = Array.from(new Set(nodes.map(n => n.type))).filter(t => typeof t === 'string' && t.trim().length > 0);
          onResourceNodeTypesDiscovered(types);
        }
      }
    } catch (error) {
      console.error('Failed to fetch world overview:', error);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey, onResourceNodeTypesDiscovered]);

  useEffect(() => {
    void fetchMapData();
  }, [fetchMapData]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setAnimTick((t: number) => t + 1);
    }, 250);

    return () => window.clearInterval(interval);
  }, []);

  // Realtime auto-refresh (every 2 seconds when enabled)
  useEffect(() => {
    if (!isRealtime) return;
    const interval = setInterval(() => void fetchMapData(), 2000);
    return () => clearInterval(interval);
  }, [isRealtime, fetchMapData]);

  // Seeded random for consistent island rendering
  const seededRandom = (seed: number, offset: number): number => {
    const x = Math.sin(seed + offset) * 10000;
    return x - Math.floor(x);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mapData) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Deep ocean background
    ctx.fillStyle = '#0a1628';
    ctx.fillRect(0, 0, MAP_CANVAS_SIZE, MAP_CANVAS_SIZE);

    // Subtle wave pattern overlay
    const waveSize = 20;
    for (let y = 0; y < MAP_CANVAS_SIZE; y += waveSize) {
      for (let x = 0; x < MAP_CANVAS_SIZE; x += waveSize) {
        const noise = Math.sin(x * 0.05) * Math.cos(y * 0.05);
        const alpha = 0.03 + noise * 0.02;
        ctx.fillStyle = `rgba(30, 58, 95, ${Math.abs(alpha)})`;
        ctx.fillRect(x, y, waveSize, waveSize);
      }
    }

    if (filters.showWhirlpools) {
      // Draw whirlpools (bright glowing cyan pixels)
      for (const whirlpool of mapData.whirlpools) {
        const x = whirlpool.x * SCALE;
        const y = whirlpool.y * SCALE;

        const pulse = (Math.sin(animTick * 0.5) + 1) / 2;
        const glowAlpha = 0.35 + pulse * 0.45;

        ctx.fillStyle = `rgba(0, 255, 255, ${glowAlpha})`;
        ctx.fillRect(x - 2, y - 2, 5, 5);

        ctx.fillStyle = `rgba(255, 255, 255, ${0.25 + pulse * 0.35})`;
        ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
      }
    }

    if (filters.showCombatHotspots) {
      // Draw combat hotspots (flashing red glow)
      const isFlashOn = animTick % 2 === 0;
      for (const hotspot of mapData.combatHotspots) {
        const x = hotspot.x * SCALE;
        const y = hotspot.y * SCALE;

        const alpha = isFlashOn ? 0.7 : 0.25;
        ctx.fillStyle = `rgba(255, 0, 0, ${alpha})`;
        ctx.fillRect(x - 2, y - 2, 5, 5);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
      }
    }

    // Draw region overlays (matching WorldMapScene)
    WORLD_REGIONS.forEach(region => {
      const x = region.minX * SCALE;
      const y = region.minY * SCALE;
      const w = (region.maxX - region.minX) * SCALE;
      const h = (region.maxY - region.minY) * SCALE;
      
      // Region fill
      ctx.fillStyle = region.color + '1a'; // 10% opacity
      ctx.fillRect(x, y, w, h);
      
      // Region border
      ctx.strokeStyle = region.color + '66'; // 40% opacity
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);
      
      // Region label
      ctx.fillStyle = region.color;
      ctx.font = '8px "Sometype Mono", monospace';
      ctx.textAlign = 'center';
      ctx.fillText(region.name, x + w / 2, y + h / 2);
    });

    // Draw latitude/longitude lines through center
    const centerX = MAP_CANVAS_SIZE / 2;
    const centerY = MAP_CANVAS_SIZE / 2;
    ctx.strokeStyle = '#ffd70099';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(centerX, 0);
    ctx.lineTo(centerX, MAP_CANVAS_SIZE);
    ctx.moveTo(0, centerY);
    ctx.lineTo(MAP_CANVAS_SIZE, centerY);
    ctx.stroke();

    // Center marker
    ctx.fillStyle = '#ffd700';
    ctx.fillRect(centerX - 2, centerY - 2, 4, 4);

    if (filters.showIslands) {
      // Draw islands with biome-accurate colors
      for (const island of mapData.islands) {
        const isClaimed: boolean = !!island.ownerId;
        if (isClaimed && !filters.showClaimedIslands) continue;
        if (!isClaimed && !island.isPermanent && !filters.showUnclaimedIslands) continue;

        const x = island.x * SCALE;
        const y = island.y * SCALE;
        const radiusPixels = Math.max(2, island.radius * SCALE);

        const biomeKey = island.biome || 'mixed';
        const colors = BIOME_PALETTE[biomeKey] || BIOME_PALETTE.mixed;
        const seed = island.x * 1000 + island.y;

        const islandIdLower = island.id.toLowerCase();
        const permanentTypeKey: string = island.isPermanent ? getPermanentTypeKey(island) : '';

        if (island.isPermanent) {
          if (permanentTypeKey === 'trading_post' && !filters.showPermanentTradingPosts) continue;
          if (permanentTypeKey === 'shipyard' && !filters.showPermanentShipyards) continue;
          if (permanentTypeKey === 'tavern' && !filters.showPermanentTaverns) continue;
          if (permanentTypeKey === 'harbor_office' && !filters.showPermanentHarborOffices) continue;
          if (permanentTypeKey === 'other' && !filters.showPermanentOther) continue;
        }

        const isTradingPostOrShipyard: boolean = island.isPermanent && (islandIdLower.includes('trading_post') || islandIdLower.includes('shipyard'));

        const drawPolygonSilhouette = (): void => {
          if (!Array.isArray(island.shape) || island.shape.length < 3) return;

          const worldPoly = island.shape.map(p => ({ x: island.x + p.x, y: island.y + p.y }));
          const minX = Math.min(...worldPoly.map(p => p.x));
          const maxX = Math.max(...worldPoly.map(p => p.x));
          const minY = Math.min(...worldPoly.map(p => p.y));
          const maxY = Math.max(...worldPoly.map(p => p.y));

          const maxDist = Math.max(...worldPoly.map(p => Math.sqrt((p.x - island.x) ** 2 + (p.y - island.y) ** 2)));

          const startTileX = Math.floor(minX / TILE_SIZE);
          const endTileX = Math.ceil(maxX / TILE_SIZE);
          const startTileY = Math.floor(minY / TILE_SIZE);
          const endTileY = Math.ceil(maxY / TILE_SIZE);

          for (let ty = startTileY; ty <= endTileY; ty++) {
            for (let tx = startTileX; tx <= endTileX; tx++) {
              const centerWorldX = tx * TILE_SIZE + TILE_SIZE / 2;
              const centerWorldY = ty * TILE_SIZE + TILE_SIZE / 2;
              if (!pointInPolygon(centerWorldX, centerWorldY, worldPoly)) continue;

              const distFromCenter = Math.sqrt((centerWorldX - island.x) ** 2 + (centerWorldY - island.y) ** 2);
              const edgeRatio = maxDist > 0 ? distFromCenter / maxDist : 0;

              let color: string;
              if (edgeRatio > 0.85) {
                color = colors.shore;
              } else {
                color = seededRandom(seed, tx * 30 + ty * 30) > 0.5 ? colors.main : colors.accent;
              }

              const px = (tx * TILE_SIZE) * SCALE;
              const py = (ty * TILE_SIZE) * SCALE;
              ctx.fillStyle = color;
              ctx.fillRect(px, py, 1, 1);
            }
          }
        };

        if (isTradingPostOrShipyard) {
          // Draw trading post/shipyard marker
          const isShipyard = islandIdLower.includes('shipyard');
          const markerColor = isShipyard ? '#ffa500' : '#87ceeb';

          // Safe zone glow
          ctx.fillStyle = markerColor + '22';
          ctx.fillRect(x - 8, y - 8, 16, 16);

          // Plus marker
          ctx.fillStyle = markerColor;
          ctx.fillRect(x - 1, y - 3, 2, 6); // vertical
          ctx.fillRect(x - 3, y - 1, 6, 2); // horizontal
        } else if (island.isPermanent) {
          drawPolygonSilhouette();
          // Permanent location - gold glow
          ctx.fillStyle = '#ffd70044';
          ctx.fillRect(x - radiusPixels - 2, y - radiusPixels - 2, radiusPixels * 2 + 4, radiusPixels * 2 + 4);
          ctx.strokeStyle = '#ffd700';
          ctx.lineWidth = 1;
          ctx.strokeRect(x - radiusPixels, y - radiusPixels, radiusPixels * 2, radiusPixels * 2);
        } else if (isClaimed) {
          drawPolygonSilhouette();

          const ownerTypeLower: string = String(island.ownerType || '').toLowerCase();
          const ownerNameLower: string = String(island.ownerName || '').toLowerCase();
          const ownerIdLower: string = String(island.ownerId || '').toLowerCase();

          // Determine faction/owner type and color
          let territoryColor: string = '#3498db'; // Default player blue
          let showTerritory: boolean = false;

          if (ownerTypeLower === 'faction_regime' || ownerTypeLower === 'admin' || ownerTypeLower === 'regime' || 
              ownerNameLower.includes('regime') || ownerIdLower.includes('regime')) {
            territoryColor = '#e74c3c'; // Red for Regime
            showTerritory = filters.showRegimeTerritory;
          } else if (ownerTypeLower === 'faction_navy' || ownerIdLower.includes('faction_navy')) {
            territoryColor = '#3b82f6'; // Blue for Navy
            showTerritory = filters.showPlayerTerritory; // Use player territory filter for factions too
          } else if (ownerTypeLower === 'faction_pirate' || ownerIdLower.includes('faction_pirate')) {
            territoryColor = '#ef4444'; // Red for Pirates
            showTerritory = filters.showPlayerTerritory;
          } else if (ownerTypeLower === 'faction_merchant' || ownerIdLower.includes('faction_merchant')) {
            territoryColor = '#10b981'; // Green for Merchant Guild
            showTerritory = filters.showPlayerTerritory;
          } else if (ownerTypeLower === 'faction_smuggler' || ownerIdLower.includes('faction_smuggler')) {
            territoryColor = '#8b5cf6'; // Purple for Smugglers
            showTerritory = filters.showPlayerTerritory;
          } else if (ownerTypeLower === 'player' || (!ownerTypeLower && !ownerIdLower.includes('faction'))) {
            territoryColor = '#3498db'; // Blue for players
            showTerritory = filters.showPlayerTerritory;
          }

          if (showTerritory) {
            const claimRadius: number | null = typeof island.claimRadius === 'number' ? island.claimRadius : null;
            const territoryRadiusPx: number = claimRadius ? Math.max(3, claimRadius * SCALE) : radiusPixels + 3;

            // Draw territory zone with faction color
            ctx.fillStyle = `${territoryColor}22`;
            ctx.fillRect(x - territoryRadiusPx, y - territoryRadiusPx, territoryRadiusPx * 2, territoryRadiusPx * 2);
            ctx.strokeStyle = `${territoryColor}aa`;
            ctx.lineWidth = 1;
            ctx.strokeRect(x - territoryRadiusPx, y - territoryRadiusPx, territoryRadiusPx * 2, territoryRadiusPx * 2);

            // Add owner label for faction territories
            if (island.ownerName && ownerIdLower.includes('faction_')) {
              const labelX = x;
              const labelY = y - territoryRadiusPx - 8;
              ctx.fillStyle = territoryColor;
              ctx.font = 'bold 7px monospace';
              ctx.textAlign = 'center';
              ctx.fillText(island.ownerName.substring(0, 12), labelX, labelY);
            }
          }
        } else {
          // Regular island
          drawPolygonSilhouette();

          // Fallback: radius-based blob if we have no shape
          if (!Array.isArray(island.shape) || island.shape.length < 3) {
            const tileSize = 1;
            const tiles = Math.max(2, Math.round(radiusPixels));

            for (let dy = -tiles; dy <= tiles; dy++) {
              for (let dx = -tiles; dx <= tiles; dx++) {
                const dist = Math.sqrt(dx * dx + dy * dy);
                const noise = seededRandom(seed, dx * 100 + dy) * 0.5;
                const threshold = tiles * (0.8 + noise * 0.4);
                if (dist <= threshold) {
                  const edgeDist = threshold - dist;
                  let color: string;

                  if (edgeDist < 0.8) {
                    color = colors.shore;
                  } else {
                    color = seededRandom(seed, dx * 30 + dy * 30) > 0.5 ? colors.main : colors.accent;
                  }

                  ctx.fillStyle = color;
                  ctx.fillRect(
                    x + dx * tileSize - tileSize / 2,
                    y + dy * tileSize - tileSize / 2,
                    tileSize,
                    tileSize
                  );
                }
              }
            }
          }
        }
      }
    }

    const nodes = Array.isArray(mapData.resourceNodes) ? mapData.resourceNodes : [];
    const enabledResourceNodeTypes = filters.enabledResourceNodeTypes;
    if (nodes.length > 0 && Object.keys(enabledResourceNodeTypes).length > 0) {
      for (const node of nodes) {
        if (!enabledResourceNodeTypes[node.type]) continue;
        const x = node.x * SCALE;
        const y = node.y * SCALE;
        ctx.fillStyle = '#f59e0b';
        ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
      }
    }

    if (filters.showNpcs) {
      // Draw NPCs (small red dots)
      ctx.fillStyle = '#e74c3c';
      for (const npc of mapData.npcs) {
        const x = npc.x * SCALE;
        const y = npc.y * SCALE;
        ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
      }
    }

    if (filters.showPlayers) {
      // Draw players (green squares with pulse effect)
      for (const player of mapData.players) {
        const x = player.x * SCALE;
        const y = player.y * SCALE;

        // Outer glow
        ctx.fillStyle = '#2ecc7144';
        ctx.fillRect(x - 3, y - 3, 6, 6);

        // Inner square
        ctx.fillStyle = '#2ecc71';
        ctx.fillRect(x - 1.5, y - 1.5, 3, 3);

        if (filters.showPlayerNames && typeof player.name === 'string' && player.name.trim().length > 0) {
          ctx.font = '7px "Sometype Mono", monospace';
          ctx.textAlign = 'left';
          ctx.fillStyle = '#b7f7c2';
          ctx.fillText(player.name, x + 4, y - 2);
        }
      }
    }

    if (filters.showRiftStorms) {
      // Draw rift storms (square zones)
      const storms = Array.isArray(mapData.riftStorms) ? mapData.riftStorms : [];
      for (const storm of storms) {
        const x = storm.x * SCALE;
        const y = storm.y * SCALE;
        const radiusPx = storm.radius * SCALE;

        // Parse hex color
        const color = typeof storm.color === 'string' ? storm.color : '#9b59b6';

        // Zone border
        ctx.strokeStyle = `${color}cc`;
        ctx.lineWidth = 1;
        ctx.fillStyle = `${color}22`;
        ctx.fillRect(x - radiusPx, y - radiusPx, radiusPx * 2, radiusPx * 2);
        ctx.strokeRect(x - radiusPx, y - radiusPx, radiusPx * 2, radiusPx * 2);

        // Inner core marker (2x2)
        ctx.fillStyle = color;
        ctx.fillRect(x - 1, y - 1, 2, 2);
      }
    }

    // Draw world boundary
    ctx.strokeStyle = '#e74c3ccc';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, MAP_CANVAS_SIZE - 2, MAP_CANVAS_SIZE - 2);
  }, [mapData, animTick, filters]);

  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    // Convert CSS pixels -> canvas pixels (canvas is scaled via CSS)
    const canvasX = (cssX / rect.width) * MAP_CANVAS_SIZE;
    const canvasY = (cssY / rect.height) * MAP_CANVAS_SIZE;

    const worldX = Math.round(canvasX / SCALE);
    const worldY = Math.round(canvasY / SCALE);
    
    setContextMenu({ x: e.clientX, y: e.clientY, worldX, worldY });
  };

  const handleTeleportClick = (): void => {
    if (contextMenu && onTeleport) {
      onTeleport(contextMenu.worldX, contextMenu.worldY);
    }
    setContextMenu(null);
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = (): void => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={MAP_CANVAS_SIZE}
        height={MAP_CANVAS_SIZE}
        onContextMenu={handleContextMenu}
        style={{
          border: '1px solid #4a5568',
          imageRendering: 'pixelated',
          width: MAP_DISPLAY_SIZE,
          height: MAP_DISPLAY_SIZE,
          cursor: selectedPlayerId ? 'crosshair' : 'default',
        }}
      />
      {contextMenu && selectedPlayerId && (
        <div
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            backgroundColor: '#1a1a2e',
            border: '1px solid #4a5568',
            padding: 4,
            zIndex: 1000,
          }}
        >
          <button
            type="button"
            onClick={handleTeleportClick}
            style={{
              padding: '6px 12px',
              fontSize: 11,
              backgroundColor: '#2d3748',
              color: '#e2e8f0',
              border: 'none',
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Teleport to ({contextMenu.worldX}, {contextMenu.worldY})
          </button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        <span style={{ fontSize: 10, color: '#a0aec0' }}>
          {mapData ? `${mapData.totalIslands} islands, ${mapData.totalPlayers} players, ${mapData.totalNpcs} NPCs, ${mapData.totalWhirlpools} whirlpools, ${mapData.totalCombatHotspots} combat` : 'Loading...'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#a0aec0', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isRealtime}
              onChange={(e) => setIsRealtime(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Realtime
          </label>
          <button
            type="button"
            onClick={() => void fetchMapData()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              fontSize: 10,
              backgroundColor: isLoading ? '#1f2937' : '#2d3748',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              cursor: isLoading ? 'default' : 'pointer',
            }}
          >
            {isLoading ? '...' : 'Refresh'}
          </button>
        </div>
      </div>
      {lastUpdate && (
        <span style={{ fontSize: 10, color: isRealtime ? '#2ecc71' : '#718096' }}>
          {isRealtime ? '🔴 LIVE' : ''} Updated: {lastUpdate}
        </span>
      )}
    </div>
  );
};

// EVE Online-style mini sparkline chart
const SparklineChart: React.FC<{
  data: number[];
  width?: number;
  height?: number;
  color?: string;
}> = ({ data, width = 120, height = 32, color = '#00ff88' }) => {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={width} cy={height - ((data[data.length - 1] - min) / range) * height} r="3" fill={color} />
    </svg>
  );
};

// EVE Online-style horizontal bar chart
const HorizontalBarChart: React.FC<{
  data: Array<{ label: string; value: number; color?: string }>;
}> = ({ data }) => {
  const max = Math.max(...data.map(d => d.value)) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {data.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 70, fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#a0aec0', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </div>
          <div style={{ flex: 1, height: 10, backgroundColor: '#1a1a2e', position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${(item.value / max) * 100}%`, backgroundColor: item.color || '#00ff88' }} />
          </div>
          <div style={{ width: 40, fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#e2e8f0', textAlign: 'right' }}>
            {item.value.toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

// Stock ticker-style value display
const TickerValue: React.FC<{
  label: string;
  value: number | string;
  prefix?: string;
  color?: string;
}> = ({ label, value, prefix = '', color }) => {
  const displayValue = typeof value === 'number' ? value.toLocaleString() : value;
  return (
    <div style={{ padding: '8px 12px', backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', minWidth: 90 }}>
      <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 14, fontWeight: 600, color: color || '#e2e8f0' }}>
        {prefix}{displayValue}
      </div>
    </div>
  );
};

// Faction distribution bar
const FactionBar: React.FC<{ data: { navy: number; pirate: number; bandit: number } }> = ({ data }) => {
  const total = data.navy + data.pirate + data.bandit || 1;
  const colors = { navy: '#3b82f6', pirate: '#ef4444', bandit: '#f59e0b' };
  return (
    <div>
      <div style={{ display: 'flex', height: 8, marginBottom: 8 }}>
        <div style={{ width: `${(data.navy / total) * 100}%`, backgroundColor: colors.navy }} />
        <div style={{ width: `${(data.pirate / total) * 100}%`, backgroundColor: colors.pirate }} />
        <div style={{ width: `${(data.bandit / total) * 100}%`, backgroundColor: colors.bandit }} />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        {Object.entries(data).map(([faction, count]) => (
          <div key={faction} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, backgroundColor: colors[faction as keyof typeof colors] }} />
            <span style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#a0aec0' }}>{faction}: {count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ProductionAdminPanel: React.FC<ProductionAdminPanelProps> = ({ apiBaseUrl }) => {
  const { confirm } = useConfirm();
  const [adminKey, setAdminKey] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [state, setState] = useState<AdminActionState>({
    isBusy: false,
    lastMessage: null,
    messageType: 'info',
  });
  const [serverStats, setServerStats] = useState<ServerStats | null>(null);
  const [economyStats, setEconomyStats] = useState<EconomyStats | null>(null);
  const [economyHistory, setEconomyHistory] = useState<HistoricalDataPoint[]>([]);
  const [broadcastMessage, setBroadcastMessage] = useState<string>('');
  const [broadcastType, setBroadcastType] = useState<string>('info');
  const [newSeed, setNewSeed] = useState<string>('');

  const [collisionDebug, setCollisionDebug] = useState<CollisionDebugData | null>(null);

  const [mapFilters, setMapFilters] = useState<AdminMapFilters>({
    showIslands: true,
    showPermanentTradingPosts: true,
    showPermanentShipyards: true,
    showPermanentTaverns: true,
    showPermanentHarborOffices: true,
    showPermanentOther: true,
    showClaimedIslands: true,
    showUnclaimedIslands: true,
    showPlayerTerritory: true,
    showRegimeTerritory: true,
    showWhirlpools: true,
    showCombatHotspots: true,
    showPlayers: true,
    showPlayerNames: false,
    showNpcs: true,
    showRiftStorms: true,
    enabledResourceNodeTypes: {},
  });

  const handleResourceNodeTypesDiscovered = (types: string[]): void => {
    setMapFilters((prev: AdminMapFilters) => {
      if (!types || types.length === 0) return prev;

      const nextEnabled: Record<string, boolean> = { ...prev.enabledResourceNodeTypes };
      let changed: boolean = false;

      // Add any new types defaulting to ON
      for (const t of types) {
        if (typeof nextEnabled[t] !== 'boolean') {
          nextEnabled[t] = true;
          changed = true;
        }
      }

      // If we still have no types configured, initialize everything ON
      if (Object.keys(prev.enabledResourceNodeTypes).length === 0 && types.length > 0) {
        changed = true;
      }

      if (!changed) return prev;
      return { ...prev, enabledResourceNodeTypes: nextEnabled };
    });
  };

  const [historyTab, setHistoryTab] = useState<'online' | 'legends' | 'recent'>('online');
  const [worldHistory, setWorldHistory] = useState<Array<WorldHistoryCharacter>>([]);

  const [accountSearchQuery, setAccountSearchQuery] = useState<string>('');
  const [accountResults, setAccountResults] = useState<Array<AdminAccountSummary>>([]);
  const [characterSearchQuery, setCharacterSearchQuery] = useState<string>('');
  const [characterResults, setCharacterResults] = useState<Array<AdminCharacterSummary>>([]);

  const [banDuration, setBanDuration] = useState<string>('72h');
  const [currencyAmount, setCurrencyAmount] = useState<string>('1000');

  const [territoriesQuery, setTerritoriesQuery] = useState<string>('');
  const [territoriesList, setTerritoriesList] = useState<Array<AdminTerritoryIsland>>([]);
  const [territoriesLastLoadedAt, setTerritoriesLastLoadedAt] = useState<string>('');
  const [territoryNameEdits, setTerritoryNameEdits] = useState<Record<string, string>>({});
  const [claimIslandId, setClaimIslandId] = useState<string>('');
  const [claimForceOverwrite, setClaimForceOverwrite] = useState<boolean>(false);

  const [constructionWipeReason, setConstructionWipeReason] = useState<string>('');
  const [constructionWipeAccountId, setConstructionWipeAccountId] = useState<string>('');
  const [constructionWipeCharacterId, setConstructionWipeCharacterId] = useState<string>('');

  // Live player management state
  const [livePlayersOpen, setLivePlayersOpen] = useState<boolean>(false);
  
  // Faction & NPC tracking state
  const [factionStats, setFactionStats] = useState<FactionStats | null>(null);
  const [npcStats, setNpcStats] = useState<NPCStats | null>(null);
  const [factionPanelOpen, setFactionPanelOpen] = useState<boolean>(false);
  
  // Market Bot panel state
  const [marketBotPanelOpen, setMarketBotPanelOpen] = useState<boolean>(false);
  
  // Rift Management panel state
  const [riftPanelOpen, setRiftPanelOpen] = useState<boolean>(false);
  const [livePlayers, setLivePlayers] = useState<Array<{ id: string; name: string; level: number; pieces: number; hp: string; shipType: string }>>([]);
  const [selectedLivePlayer, setSelectedLivePlayer] = useState<string>('');
  const [livePlayerFilter, setLivePlayerFilter] = useState<string>('');
  const [liveItems, setLiveItems] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [liveGiveItemId, setLiveGiveItemId] = useState<string>('');
  const [liveGiveQty, setLiveGiveQty] = useState<string>('1');
  const [livePiecesAmount, setLivePiecesAmount] = useState<string>('');
  const [liveLevelAmount, setLiveLevelAmount] = useState<string>('');

  // Guest session history state
  const [guestStats, setGuestStats] = useState<GuestStats | null>(null);
  const [guestPanelOpen, setGuestPanelOpen] = useState<boolean>(false);

  const verifyKey = async (key: string): Promise<boolean> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminKey: key }),
      });
      if (response.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminKey', key);
        fetchServerStats(key);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleFullWorldReset = async (): Promise<void> => {
    if (!adminKey) {
      setState({ isBusy: false, lastMessage: 'Admin key required', messageType: 'error' });
      return;
    }

    const confirmed = await confirm({
      title: 'Full World Reset',
      message: 'This will delete and regenerate all islands, chunks, and permanent locations. This cannot be undone. Continue?',
      confirmText: 'Reset World',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    await callEndpoint('/api/admin/prod/full-reset', 'Full World Reset');
  };

  const handleLogin = async (): Promise<void> => {
    if (!adminKey.trim()) {
      setState({ isBusy: false, lastMessage: 'Enter admin key', messageType: 'error' });
      return;
    }
    setState({ isBusy: true, lastMessage: 'Verifying...', messageType: 'info' });
    const valid = await verifyKey(adminKey);
    if (valid) {
      setState({ isBusy: false, lastMessage: 'Authenticated', messageType: 'success' });
    } else {
      setState({ isBusy: false, lastMessage: 'Invalid admin key', messageType: 'error' });
    }
  };

  const handleLogout = (): void => {
    setIsAuthenticated(false);
    setAdminKey('');
    sessionStorage.removeItem('adminKey');
    setServerStats(null);
  };

  const fetchServerStats = useCallback(async (key: string): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/stats`, {
        headers: {
          'x-admin-key': key,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setServerStats(data);
      }
    } catch {
      // Ignore stats fetch errors
    }
  }, [apiBaseUrl]);

  const fetchWorldHistory = useCallback(async (key: string, tab: 'online' | 'legends' | 'recent'): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/world-history?tab=${encodeURIComponent(tab)}&limit=50&adminKey=${encodeURIComponent(key)}`);
      if (response.ok) {
        const data: { characters?: Array<WorldHistoryCharacter> } = await response.json();
        setWorldHistory(Array.isArray(data.characters) ? data.characters : []);
      }
    } catch {
      // ignore
    }
  }, [apiBaseUrl]);

  const fetchEconomyStats = useCallback(async (key: string): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/economy`, {
        headers: {
          'x-admin-key': key,
        },
      });
      if (response.ok) {
        const data: EconomyStats = await response.json();
        setEconomyStats(data);
        setEconomyHistory(prev => [...prev, { timestamp: data.timestamp, value: data.currency.totalPlayerPieces }].slice(-20));
      }
    } catch {
      // Ignore economy fetch errors
    }
  }, [apiBaseUrl]);

  const fetchFactionStats = useCallback(async (key: string): Promise<void> => {
    try {
      const headers = { 'x-admin-key': key };
      const [factionRes, npcRes] = await Promise.all([
        fetch(`${apiBaseUrl}/api/admin/prod/factions`, { headers }),
        fetch(`${apiBaseUrl}/api/admin/prod/ambient-npcs`, { headers }),
      ]);
      if (factionRes.ok) {
        const data = await factionRes.json();
        setFactionStats(data);
      }
      if (npcRes.ok) {
        const data = await npcRes.json();
        setNpcStats(data);
      }
    } catch {
      // Ignore faction fetch errors
    }
  }, [apiBaseUrl]);

  const fetchGuestStats = useCallback(async (key: string): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/guest-metrics?days=7`, {
        headers: {
          'x-admin-key': key,
        },
      });
      if (response.ok) {
        const data: GuestStats = await response.json();
        setGuestStats(data);
      }
    } catch {
      // Ignore guest stats fetch errors
    }
  }, [apiBaseUrl]);

  // Verify admin key on mount if stored
  useEffect(() => {
    const storedKey = sessionStorage.getItem('adminKey');
    if (storedKey) {
      setAdminKey(storedKey);
      (async () => {
        try {
          const response = await fetch(`${apiBaseUrl}/api/admin/prod/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ adminKey: storedKey }),
          });
          if (response.ok) {
            setIsAuthenticated(true);
            await fetchServerStats(storedKey);
            await fetchEconomyStats(storedKey);
          }
        } catch {
          // Ignore verification errors on mount
        }
      })();
    }
  }, [apiBaseUrl, fetchServerStats, fetchEconomyStats]);

  // Auto-refresh stats every 10 seconds
  useEffect(() => {
    if (!isAuthenticated || !adminKey) return;
    const interval = setInterval(() => {
      fetchEconomyStats(adminKey);
      fetchServerStats(adminKey);
    }, 10000);
    return () => clearInterval(interval);
  }, [isAuthenticated, adminKey, fetchEconomyStats, fetchServerStats]);

  useEffect(() => {
    if (!isAuthenticated || !adminKey) return;
    void fetchWorldHistory(adminKey, historyTab);
  }, [isAuthenticated, adminKey, historyTab, fetchWorldHistory]);

  const doAccountSearch = async (): Promise<void> => {
    const q = accountSearchQuery.trim();
    if (!q) {
      setAccountResults([]);
      setState({ isBusy: false, lastMessage: 'Enter an account search query', messageType: 'error' });
      return;
    }
    setState({ isBusy: true, lastMessage: 'Searching accounts...', messageType: 'info' });
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/search/accounts?q=${encodeURIComponent(q)}&limit=20&adminKey=${encodeURIComponent(adminKey)}`);
      const data: { results?: Array<AdminAccountSummary>; error?: string } = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`);
      }
      setAccountResults(Array.isArray(data.results) ? data.results : []);
      setState({ isBusy: false, lastMessage: `Found ${(data.results || []).length} accounts`, messageType: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${errorMessage}`, messageType: 'error' });
    }
  };

  const doCharacterSearch = async (): Promise<void> => {
    const q = characterSearchQuery.trim();
    if (!q) {
      setCharacterResults([]);
      setState({ isBusy: false, lastMessage: 'Enter a character search query', messageType: 'error' });
      return;
    }
    setState({ isBusy: true, lastMessage: 'Searching characters...', messageType: 'info' });
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/search/characters?q=${encodeURIComponent(q)}&limit=20&adminKey=${encodeURIComponent(adminKey)}`);
      const data: { results?: Array<AdminCharacterSummary>; error?: string } = await response.json().catch(() => ({} as any));
      if (!response.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`);
      }
      setCharacterResults(Array.isArray(data.results) ? data.results : []);
      setState({ isBusy: false, lastMessage: `Found ${(data.results || []).length} characters`, messageType: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${errorMessage}`, messageType: 'error' });
    }
  };

  const confirmAction = (label: string): Promise<boolean> => {
    return confirm({
      title: 'Confirm',
      message: `Confirm: ${label}`,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      tone: 'danger',
    });
  };

  const clearAllConstructions = async (): Promise<void> => {
    if (!(await confirmAction('WIPE ALL player-placed tiles (constructions) across the entire world?'))) return;
    if (!(await confirmAction('CRITICAL: This cannot be undone. Proceed with global construction wipe?'))) return;
    await callEndpoint('/api/admin/prod/constructions/clear-all', 'Clear All Player-Placed Tiles', {
      adminId: 'production_admin_panel',
      reason: constructionWipeReason.trim() || undefined,
    });
  };

  const clearAccountConstructions = async (): Promise<void> => {
    const accountId = constructionWipeAccountId.trim();
    if (!accountId) {
      setState({ isBusy: false, lastMessage: 'Enter an accountId', messageType: 'error' });
      return;
    }
    if (!(await confirmAction(`Wipe ALL player-placed tiles for account ${accountId.substring(0, 8)}...?`))) return;
    await callEndpoint('/api/admin/prod/constructions/clear-account', 'Clear Account Player-Placed Tiles', {
      accountId,
      adminId: 'production_admin_panel',
      reason: constructionWipeReason.trim() || undefined,
    });
  };

  const clearCharacterConstructions = async (): Promise<void> => {
    const characterId = constructionWipeCharacterId.trim();
    if (!characterId) {
      setState({ isBusy: false, lastMessage: 'Enter a characterId', messageType: 'error' });
      return;
    }
    if (!(await confirmAction(`Wipe ALL player-placed tiles for character ${characterId.substring(0, 8)}...?`))) return;
    await callEndpoint('/api/admin/prod/constructions/clear-character', 'Clear Character Player-Placed Tiles', {
      characterId,
      adminId: 'production_admin_panel',
      reason: constructionWipeReason.trim() || undefined,
    });
  };

  const banAccount = async (accountId: string): Promise<void> => {
    if (!(await confirmAction(`Ban account ${accountId.substring(0, 8)}... for ${banDuration || '72h'}?`))) return;
    await callEndpoint('/api/admin/prod/account/ban', 'Ban Account', { accountId, duration: banDuration || '72h' });
  };

  const unbanAccount = async (accountId: string): Promise<void> => {
    if (!(await confirmAction(`Unban account ${accountId.substring(0, 8)}...?`))) return;
    await callEndpoint('/api/admin/prod/account/unban', 'Unban Account', { accountId });
  };

  const setOrAddBerries = async (characterId: string, mode: 'set' | 'add'): Promise<void> => {
    const amt = Number.parseInt(currencyAmount, 10);
    if (Number.isNaN(amt)) {
      setState({ isBusy: false, lastMessage: 'Enter a valid currency amount', messageType: 'error' });
      return;
    }
    const verb = mode === 'add' ? 'Add' : 'Set';
    if (!(await confirmAction(`${verb} ₱${amt.toLocaleString()} for character ${characterId.substring(0, 8)}...?`))) return;
    await callEndpoint('/api/admin/prod/character/berries', `${verb} Currency`, { characterId, amount: amt, mode });
  };

  const callEndpoint = async (
    path: string,
    label: string,
    body?: Record<string, unknown>,
    method: 'POST' | 'GET' = 'POST'
  ): Promise<void> => {
    setState({ isBusy: true, lastMessage: `${label}...`, messageType: 'info' });

    try {
      const init: RequestInit = { 
        method,
        headers: {
          'x-admin-key': adminKey,
          'Content-Type': 'application/json',
        },
      };
      const url = `${apiBaseUrl}${path}`;

      if (method === 'POST') {
        init.body = JSON.stringify(body || {});
      }

      const response = await fetch(url, init);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMsg = typeof data?.error === 'string' ? data.error : `HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      const message = typeof data?.message === 'string' ? data.message : `${label} completed`;
      setState({ isBusy: false, lastMessage: message, messageType: 'success' });

      // Refresh stats after actions
      fetchServerStats(adminKey);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${errorMessage}`, messageType: 'error' });
    }
  };

  const fetchCollisionDebug = async (): Promise<void> => {
    if (!apiBaseUrl || !adminKey) return;

    setState({ isBusy: true, lastMessage: 'Loading collision debug...', messageType: 'info' });
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/admin/prod/collision-debug?adminKey=${encodeURIComponent(adminKey)}`
      );
      const data: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMsg: string =
          typeof (data as { error?: unknown } | null)?.error === 'string'
            ? String((data as { error?: unknown }).error)
            : `HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      setCollisionDebug(data as CollisionDebugData);
      const message: string =
        typeof (data as { message?: unknown } | null)?.message === 'string'
          ? String((data as { message?: unknown }).message)
          : 'Collision debug loaded';
      setState({ isBusy: false, lastMessage: message, messageType: 'success' });
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${errorMessage}`, messageType: 'error' });
    }
  };

  const handleBroadcast = (): void => {
    if (!broadcastMessage.trim()) {
      setState({ isBusy: false, lastMessage: 'Enter a message to broadcast', messageType: 'error' });
      return;
    }
    void callEndpoint('/api/admin/prod/broadcast', 'Broadcast', {
      message: broadcastMessage,
      type: broadcastType,
    });
    setBroadcastMessage('');
  };

  const handleRegenerate = (): void => {
    const seed = newSeed.trim() ? parseInt(newSeed, 10) : undefined;
    void callEndpoint('/api/admin/prod/full-reset', 'Full Reset', seed ? { newSeed: seed } : {});
    setNewSeed('');
  };

  // Live player management functions (uses prod endpoints)
  const fetchLivePlayers = useCallback(async (): Promise<void> => {
    try {
      setState({ isBusy: true, lastMessage: 'Loading live players...', messageType: 'info' });
      const headers = { 'x-admin-key': adminKey };
      const res = await fetch(`${apiBaseUrl}/api/admin/prod/players/online`, { headers });
      const data = await res.json();
      if (data.players) {
        setLivePlayers(data.players);
        setState({ isBusy: false, lastMessage: `Found ${data.count} online players`, messageType: 'success' });
      }
      const itemsRes = await fetch(`${apiBaseUrl}/api/admin/prod/items/list`, { headers });
      const itemsData = await itemsRes.json();
      if (itemsData.items) setLiveItems(itemsData.items);
    } catch (err) {
      setState({ isBusy: false, lastMessage: `Error: ${err}`, messageType: 'error' });
    }
  }, [apiBaseUrl, adminKey]);

  // Auto-fetch live players for teleport feature
  useEffect(() => {
    if (!isAuthenticated) return;
    void fetchLivePlayers();
    const interval = setInterval(() => void fetchLivePlayers(), 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchLivePlayers]);

  const setFilterValue = (key: keyof AdminMapFilters, value: boolean): void => {
    setMapFilters((prev: AdminMapFilters) => ({ ...prev, [key]: value }));
  };

  const toggleResourceNodeType = (type: string): void => {
    setMapFilters((prev: AdminMapFilters) => ({
      ...prev,
      enabledResourceNodeTypes: {
        ...prev.enabledResourceNodeTypes,
        [type]: !prev.enabledResourceNodeTypes[type],
      },
    }));
  };

  const fetchClaimedTerritories = useCallback(async (): Promise<void> => {
    if (!apiBaseUrl || !adminKey) return;
    try {
      setState({ isBusy: true, lastMessage: 'Loading claimed territories...', messageType: 'info' });
      const res = await fetch(`${apiBaseUrl}/api/admin/prod/territory/claimed`, {
        headers: {
          'x-admin-key': adminKey,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${res.status}`);
      }
      const islands: Array<AdminTerritoryIsland> = Array.isArray(data?.islands) ? data.islands : [];
      setTerritoriesList(islands);
      setTerritoriesLastLoadedAt(new Date().toLocaleTimeString());
      setState({ isBusy: false, lastMessage: `Loaded ${islands.length} claimed territories`, messageType: 'success' });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${msg}`, messageType: 'error' });
    }
  }, [apiBaseUrl, adminKey]);

  const searchTerritories = useCallback(async (): Promise<void> => {
    if (!apiBaseUrl || !adminKey) return;
    const q: string = territoriesQuery.trim();
    if (q.length < 2) {
      setState({ isBusy: false, lastMessage: 'Search query must be at least 2 characters', messageType: 'error' });
      return;
    }
    try {
      setState({ isBusy: true, lastMessage: 'Searching islands...', messageType: 'info' });
      const res = await fetch(`${apiBaseUrl}/api/admin/prod/territory/search?q=${encodeURIComponent(q)}&adminKey=${encodeURIComponent(adminKey)}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data?.error === 'string' ? data.error : `HTTP ${res.status}`);
      }
      const islands: Array<AdminTerritoryIsland> = Array.isArray(data?.islands) ? data.islands : [];
      setTerritoriesList(islands);
      setTerritoriesLastLoadedAt(new Date().toLocaleTimeString());
      setState({ isBusy: false, lastMessage: `Found ${islands.length} islands`, messageType: 'success' });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setState({ isBusy: false, lastMessage: `Error: ${msg}`, messageType: 'error' });
    }
  }, [apiBaseUrl, adminKey, territoriesQuery]);

  const claimForRegime = async (islandId: string, force: boolean): Promise<void> => {
    if (!islandId || typeof islandId !== 'string') return;
    if (!force) {
      if (!(await confirmAction(`Claim ${islandId} for The Regime?`))) return;
    } else {
      if (!(await confirmAction(`FORCE claim ${islandId} for The Regime (overwrite existing owner)?`))) return;
    }

    await callEndpoint('/api/admin/prod/territory/set-owner', 'Claim for Regime', {
      islandId,
      mode: 'regime',
      force,
    });

    void fetchClaimedTerritories();
  };

  const clearTerritoryOwner = async (islandId: string): Promise<void> => {
    await callEndpoint('/api/admin/prod/territory/set-owner', 'Clear Owner', {
      islandId,
      mode: 'clear',
    });
    void fetchClaimedTerritories();
  };

  const updateIslandName = async (islandId: string, name: string): Promise<void> => {
    await callEndpoint('/api/admin/prod/territory/set-name', 'Rename Island', {
      islandId,
      name,
    });
    setTerritoryNameEdits((prev) => ({ ...prev, [islandId]: name }));
    void fetchClaimedTerritories();
  };

  const renderMapToggle = (label: string, checked: boolean, onChange: (checked: boolean) => void): JSX.Element => {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#a0aec0', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ cursor: 'pointer' }}
        />
        {label}
      </label>
    );
  };

  const liveAction = async (playerId: string, action: string, body?: Record<string, unknown>): Promise<void> => {
    try {
      setState({ isBusy: true, lastMessage: `${action}...`, messageType: 'info' });
      const res = await fetch(`${apiBaseUrl}/api/admin/prod/players/${playerId}/${action}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
        body: JSON.stringify({ ...(body || {}), adminKey }),
      });
      const data = await res.json();
      if (res.ok) {
        setState({ isBusy: false, lastMessage: data.message || `${action} done`, messageType: 'success' });
        void fetchLivePlayers();
      } else {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    } catch (err) {
      setState({ isBusy: false, lastMessage: `Error: ${err}`, messageType: 'error' });
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="font-mono" style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#1a1a2e',
          border: '2px solid #4a5568',
          padding: 32,
          width: 360,
        }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ color: '#a0aec0', fontSize: 12, display: 'block', marginBottom: 4 }}>
              Admin Key
            </label>
            <input
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#2d3748',
                border: '1px solid #4a5568',
                color: '#e2e8f0',
                fontSize: 14,
              }}
              placeholder="Enter admin key"
            />
          </div>
          <button
            onClick={handleLogin}
            disabled={state.isBusy}
            style={{
              width: '100%',
              padding: '10px 16px',
              backgroundColor: '#ffa500',
              color: '#000',
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: state.isBusy ? 'default' : 'pointer',
              opacity: state.isBusy ? 0.7 : 1,
            }}
          >
            {state.isBusy ? 'Verifying...' : 'Login'}
          </button>
          {state.lastMessage && (
            <div style={{
              marginTop: 12,
              padding: 8,
              backgroundColor: state.messageType === 'error' ? '#742a2a' : '#2d3748',
              color: state.messageType === 'error' ? '#fed7d7' : '#e2e8f0',
              fontSize: 12,
            }}>
              {state.lastMessage}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="font-mono" style={{
      height: '100vh',
      overflowY: 'auto',
      backgroundColor: '#0a0a0f',
      padding: 24,
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #4a5568',
        }}>
          <h1 style={{ color: '#ffa500', fontSize: 24, margin: 0, fontFamily: 'var(--font-lookout)' }}>Grand Line</h1>
          <button
            onClick={handleLogout}
            style={{
              padding: '6px 12px',
              backgroundColor: '#4a5568',
              color: '#e2e8f0',
              border: 'none',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Logout
          </button>
        </div>

        {/* Server Stats */}
        {serverStats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}>
            {[
              { label: 'Players Online', value: serverStats.connectedPlayers },
              { label: 'Total Ships', value: serverStats.totalShips },
              { label: 'World Seed', value: serverStats.worldSeed },
              { label: 'Uptime', value: serverStats.uptime },
            ].map((stat) => (
              <div key={stat.label} style={{
                backgroundColor: '#1a1a2e',
                border: '1px solid #4a5568',
                padding: 16,
              }}>
                <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 4 }}>{stat.label}</div>
                <div style={{ color: '#e2e8f0', fontSize: 18, fontWeight: 600 }}>{stat.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Status Message */}
        {state.lastMessage && (
          <div style={{
            marginBottom: 16,
            padding: 12,
            backgroundColor: state.messageType === 'error' ? '#742a2a' : state.messageType === 'success' ? '#22543d' : '#2d3748',
            color: state.messageType === 'error' ? '#fed7d7' : state.messageType === 'success' ? '#9ae6b4' : '#e2e8f0',
            fontSize: 13,
          }}>
            {state.lastMessage}
          </div>
        )}

        {/* Phase 1: Bot Activity Feed */}
        <BotActivityFeed apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 1: Player Wealth Monitor */}
        <PlayerWealthMonitor apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 1: Quick Actions Sidebar */}
        <AdminQuickActions
          apiBaseUrl={apiBaseUrl}
          adminKey={adminKey}
          onBroadcast={() => {
            const msg = prompt('Enter broadcast message:');
            if (msg) void callEndpoint('/api/admin/prod/broadcast', 'Broadcast', { message: msg, type: 'info' });
          }}
          onToggleBots={() => void callEndpoint('/api/admin/prod/toggle-bots', 'Toggle Bots', {})}
          onResetEconomy={() => {
            if (window.confirm('WARNING: Reset economy? This cannot be undone!')) {
              if (window.confirm('CRITICAL: Are you absolutely sure? This will reset ALL player currency and economy state!')) {
                void callEndpoint('/api/admin/prod/reset-economy', 'Reset Economy', {});
              }
            }
          }}
          onReloadCollisions={() => void callEndpoint('/api/admin/prod/reload-collisions', 'Reload Collisions', {})}
          onFullResetWorld={() => void handleFullWorldReset()}
          onViewPlayers={() => setLivePlayersOpen(true)}
          onViewBotActivity={() => setMarketBotPanelOpen(true)}
          onResetCharacter={(playerId) => {
            if (window.confirm(`Reset character ${playerId} to core world?`)) {
              void callEndpoint('/api/admin/prod/reset-to-core-world', 'Reset Character', { playerId });
            }
          }}
          isLoading={state.isBusy}
        />

        {/* Phase 2: Alert System */}
        <AdminAlertSystem apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 3: Economy Charts & Trends */}
        <EconomyChartsAndTrends apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 3: Bot Active Orders */}
        <BotOrdersDisplay apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 2: Advanced Search */}
        <AdvancedSearch
          apiBaseUrl={apiBaseUrl}
          adminKey={adminKey}
          onSearch={(results, type) => {
            console.log(`Search results (${type}):`, results);
          }}
        />

        {/* Phase 2: Territory Ownership Summary */}
        <TerritoryOwnershipSummary apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Territory Fixer - Fix regime ownership */}
        <TerritoryFixerPanel apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Phase 2: Admin Audit Trail */}
        <AdminAuditTrail apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Mail Composer */}
        <MailComposer apiBaseUrl={apiBaseUrl} adminKey={adminKey} />

        {/* Action Sections */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
          {/* World Management */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>World Management</h2>
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #4a5568' }}>
              <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 8 }}>World Overview - Right-click to teleport selected player</div>
              {livePlayers.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <select
                    value={selectedLivePlayer}
                    onChange={(e) => setSelectedLivePlayer(e.target.value)}
                    style={{ ...inputStyle, marginBottom: 4 }}
                  >
                    <option value="">Select player to teleport...</option>
                    {livePlayers.map(p => (
                      <option key={p.id} value={p.id}>{p.name} (Lv.{p.level})</option>
                    ))}
                  </select>
                </div>
              )}
              <MiniWorldMap
                apiBaseUrl={apiBaseUrl}
                adminKey={adminKey}
                selectedPlayerId={selectedLivePlayer}
                filters={mapFilters}
                onResourceNodeTypesDiscovered={handleResourceNodeTypesDiscovered}
                onTeleport={async (worldX, worldY) => {
                  if (!selectedLivePlayer) {
                    setState({ isBusy: false, lastMessage: 'Select a player first', messageType: 'error' });
                    return;
                  }
                  const tileX = Math.floor(worldX / 16);
                  const tileY = Math.floor(worldY / 16);
                  await liveAction(selectedLivePlayer, 'teleport', { tileX, tileY });
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => callEndpoint('/api/admin/prod/reload-collisions', 'Reload Collisions')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Reload Collisions
              </button>
              <button
                onClick={() => void fetchCollisionDebug()}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Collision Debug
              </button>
              {collisionDebug && (
                <div
                  style={{
                    backgroundColor: '#0b1220',
                    border: '1px solid #4a5568',
                    padding: 10,
                    color: '#e2e8f0',
                    fontSize: 11,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {JSON.stringify(
                    {
                      collisionCacheSize: collisionDebug.collisionCacheSize,
                      totalIslands: collisionDebug.totalIslands,
                      permanentIslands: collisionDebug.permanentIslands,
                      proceduralIslands: collisionDebug.proceduralIslands,
                      sampleTiles: collisionDebug.sampleTiles,
                    },
                    null,
                    2
                  )}
                </div>
              )}
              <button
                onClick={() => callEndpoint('/api/admin/prod/reseed-permanent', 'Reseed Permanents')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Reseed Permanent Locations
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/regime/reseed-arcadia', 'Reseed Arcadia', undefined, 'POST')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Reseed Arcadia (Regime Names)
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/spawn-missing-taverns', 'Spawn Missing Taverns')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Spawn Missing Taverns
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/reset-resources', 'Reset Resources')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Reset Resource Nodes
              </button>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #4a5568' }}>
                <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 6 }}>
                  Player-Placed Tiles (Constructions)
                </div>
                <input
                  value={constructionWipeReason}
                  onChange={(e) => setConstructionWipeReason(e.target.value)}
                  placeholder="Reason (optional)"
                  style={{ ...inputStyle, marginBottom: 8 }}
                />
                <button
                  type="button"
                  onClick={() => void clearAllConstructions()}
                  disabled={state.isBusy}
                  style={{
                    ...buttonStyle(state.isBusy),
                    backgroundColor: '#742a2a',
                    borderColor: '#9b2c2c',
                  }}
                >
                  Wipe ALL Player-Placed Tiles
                </button>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    value={constructionWipeAccountId}
                    onChange={(e) => setConstructionWipeAccountId(e.target.value)}
                    placeholder="accountId (wipe all chars)"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => void clearAccountConstructions()}
                    disabled={state.isBusy || constructionWipeAccountId.trim().length === 0}
                    style={{ ...buttonStyle(state.isBusy || constructionWipeAccountId.trim().length === 0), width: 220, textAlign: 'center' }}
                  >
                    Wipe Account Tiles
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    value={constructionWipeCharacterId}
                    onChange={(e) => setConstructionWipeCharacterId(e.target.value)}
                    placeholder="characterId"
                    style={inputStyle}
                  />
                  <button
                    type="button"
                    onClick={() => void clearCharacterConstructions()}
                    disabled={state.isBusy || constructionWipeCharacterId.trim().length === 0}
                    style={{ ...buttonStyle(state.isBusy || constructionWipeCharacterId.trim().length === 0), width: 220, textAlign: 'center' }}
                  >
                    Wipe Character Tiles
                  </button>
                </div>
              </div>
              <button
                onClick={() => callEndpoint('/api/admin/prod/clear-fishing-spots', 'Clear Fishing')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Clear Fishing Spots
              </button>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #4a5568' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="number"
                    value={newSeed}
                    onChange={(e) => setNewSeed(e.target.value)}
                    placeholder="New seed (optional)"
                    style={inputStyle}
                  />
                </div>
                <button
                  onClick={handleRegenerate}
                  disabled={state.isBusy}
                  style={{
                    ...buttonStyle(state.isBusy),
                    backgroundColor: '#742a2a',
                    borderColor: '#9b2c2c',
                  }}
                >
                  Full World Reset
                </button>
              </div>
            </div>
          </div>

          {/* Territories */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Territories</h2>

            <div style={{ marginBottom: 12 }}>
              <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 6 }}>Claim for The Regime (by island catalog/id)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={claimIslandId}
                  onChange={(e) => setClaimIslandId(e.target.value)}
                  placeholder="island_#_#_#_#"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => void claimForRegime(claimIslandId.trim(), claimForceOverwrite)}
                  disabled={state.isBusy || claimIslandId.trim().length === 0}
                  style={{ ...buttonStyle(state.isBusy || claimIslandId.trim().length === 0), width: 140, textAlign: 'center' }}
                >
                  Claim
                </button>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={claimForceOverwrite}
                  onChange={(e) => setClaimForceOverwrite(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Force overwrite existing owner
              </label>
            </div>

            <div style={{ paddingTop: 12, borderTop: '1px solid #4a5568' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                <div style={{ color: '#a0aec0', fontSize: 11 }}>
                  Claimed islands / search
                  {territoriesLastLoadedAt ? ` (last: ${territoriesLastLoadedAt})` : ''}
                </div>
                <button
                  type="button"
                  onClick={() => void fetchClaimedTerritories()}
                  disabled={state.isBusy}
                  style={{ padding: '6px 10px', backgroundColor: state.isBusy ? '#1f2937' : '#2d3748', color: state.isBusy ? '#718096' : '#e2e8f0', border: '1px solid #4a5568', fontSize: 11, cursor: state.isBusy ? 'default' : 'pointer' }}
                >
                  Refresh
                </button>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input
                  value={territoriesQuery}
                  onChange={(e) => setTerritoriesQuery(e.target.value)}
                  placeholder="Search islands by id/name..."
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => void searchTerritories()}
                  disabled={state.isBusy}
                  style={{ ...buttonStyle(state.isBusy), width: 120, textAlign: 'center' }}
                >
                  Search
                </button>
              </div>

              <div style={{ border: '1px solid #4a5568', maxHeight: 320, overflow: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 0.6fr', gap: 8, padding: '8px 10px', backgroundColor: '#2d3748', color: '#a0aec0', fontSize: 10 }}>
                  <div>Island</div>
                  <div>Owner</div>
                  <div>Actions</div>
                </div>

                {territoriesList.length === 0 && (
                  <div style={{ padding: 10, color: '#718096', fontSize: 11 }}>No results. Click Refresh or run a search.</div>
                )}

                {territoriesList.map((i) => {
                  const ownerTypeLower: string = String(i.ownerType || '').toLowerCase();
                  const isRegime: boolean = ownerTypeLower === 'admin' || String(i.ownerName || '').toLowerCase().includes('regime');
                  const currentName: string = territoryNameEdits[i.id] ?? i.customName ?? i.name;
                  return (
                    <div key={i.id} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 0.6fr', gap: 8, padding: '8px 10px', borderTop: '1px solid #4a5568', backgroundColor: '#1a1a2e', color: '#e2e8f0', fontSize: 11 }}>
                      <div style={{ minWidth: 0 }}>
                        <input
                          value={currentName}
                          onChange={(e) => setTerritoryNameEdits((prev) => ({ ...prev, [i.id]: e.target.value }))}
                          placeholder={i.name}
                          style={{
                            ...inputStyle,
                            padding: '6px 8px',
                            fontSize: 12,
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button
                            type="button"
                            onClick={() => void updateIslandName(i.id, currentName)}
                            disabled={state.isBusy}
                            style={{ ...buttonStyle(state.isBusy), flex: 1, textAlign: 'center' }}
                          >
                            Save Name
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setTerritoryNameEdits((prev) => ({ ...prev, [i.id]: '' }));
                              void updateIslandName(i.id, '');
                            }}
                            disabled={state.isBusy}
                            style={{ ...buttonStyle(state.isBusy), flex: 1, textAlign: 'center', backgroundColor: '#1f2937', borderColor: '#4a5568', color: '#e2e8f0' }}
                          >
                            Clear
                          </button>
                        </div>
                        <div style={{ color: '#a0aec0', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>id: {i.id}</div>
                        <div style={{ color: '#718096', fontSize: 10 }}>({Math.round(i.position.x)}, {Math.round(i.position.y)}) | {i.biome}</div>
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: isRegime ? '#e74c3c' : '#3498db', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {i.ownerName || i.ownerId || '—'}
                        </div>
                        <div style={{ color: '#718096', fontSize: 10 }}>type: {i.ownerType || '—'}</div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button
                          type="button"
                          onClick={() => void claimForRegime(i.id, false)}
                          disabled={state.isBusy}
                          style={{ ...buttonStyle(state.isBusy), textAlign: 'center', backgroundColor: '#1f2937', borderColor: '#4a5568' }}
                        >
                          Regime
                        </button>
                        <button
                          type="button"
                          onClick={() => void claimForRegime(i.id, true)}
                          disabled={state.isBusy}
                          style={{ ...buttonStyle(state.isBusy), textAlign: 'center', backgroundColor: '#742a2a', borderColor: '#9b2c2c' }}
                        >
                          Force
                        </button>
                        <button
                          type="button"
                          onClick={() => void clearTerritoryOwner(i.id)}
                          disabled={state.isBusy}
                          style={{ ...buttonStyle(state.isBusy), textAlign: 'center' }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Broadcast */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Broadcast Message</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Enter message to broadcast to all players..."
                style={{
                  ...inputStyle,
                  minHeight: 80,
                  resize: 'vertical',
                }}
              />
              <select
                value={broadcastType}
                onChange={(e) => setBroadcastType(e.target.value)}
                style={inputStyle}
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="success">Success</option>
                <option value="event">Event</option>
              </select>
              <button
                onClick={handleBroadcast}
                disabled={state.isBusy || !broadcastMessage.trim()}
                style={buttonStyle(state.isBusy || !broadcastMessage.trim())}
              >
                Send Broadcast
              </button>

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #4a5568' }}>
                <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 10 }}>Map Layers</div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
                  {renderMapToggle('Players', mapFilters.showPlayers, (v) => setFilterValue('showPlayers', v))}
                  {renderMapToggle('Player Name Labels', mapFilters.showPlayerNames, (v) => setFilterValue('showPlayerNames', v))}
                  {renderMapToggle('NPCs', mapFilters.showNpcs, (v) => setFilterValue('showNpcs', v))}
                  {renderMapToggle('Combat', mapFilters.showCombatHotspots, (v) => setFilterValue('showCombatHotspots', v))}
                  {renderMapToggle('Whirlpools', mapFilters.showWhirlpools, (v) => setFilterValue('showWhirlpools', v))}
                  {renderMapToggle('Rift Storms', mapFilters.showRiftStorms, (v) => setFilterValue('showRiftStorms', v))}
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 8 }}>Islands</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {renderMapToggle('Draw Islands', mapFilters.showIslands, (v) => setFilterValue('showIslands', v))}
                    {renderMapToggle('Claimed', mapFilters.showClaimedIslands, (v) => setFilterValue('showClaimedIslands', v))}
                    {renderMapToggle('Unclaimed', mapFilters.showUnclaimedIslands, (v) => setFilterValue('showUnclaimedIslands', v))}
                    {renderMapToggle('Player Territory', mapFilters.showPlayerTerritory, (v) => setFilterValue('showPlayerTerritory', v))}
                    {renderMapToggle('Regime Territory', mapFilters.showRegimeTerritory, (v) => setFilterValue('showRegimeTerritory', v))}
                    {renderMapToggle('Trading Posts', mapFilters.showPermanentTradingPosts, (v) => setFilterValue('showPermanentTradingPosts', v))}
                    {renderMapToggle('Shipyards', mapFilters.showPermanentShipyards, (v) => setFilterValue('showPermanentShipyards', v))}
                    {renderMapToggle('Taverns', mapFilters.showPermanentTaverns, (v) => setFilterValue('showPermanentTaverns', v))}
                    {renderMapToggle('Harbor Offices', mapFilters.showPermanentHarborOffices, (v) => setFilterValue('showPermanentHarborOffices', v))}
                    {renderMapToggle('Other Permanents', mapFilters.showPermanentOther, (v) => setFilterValue('showPermanentOther', v))}
                  </div>
                </div>

                <div>
                  <div style={{ color: '#a0aec0', fontSize: 10, marginBottom: 8 }}>Resource Nodes</div>
                  <div style={{ color: '#718096', fontSize: 10, marginBottom: 8 }}>
                    Toggle types on/off (orange pixels)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {Object.keys(mapFilters.enabledResourceNodeTypes).length === 0 ? (
                      <span style={{ color: '#718096', fontSize: 10 }}>Waiting for map data...</span>
                    ) : (
                      Object.keys(mapFilters.enabledResourceNodeTypes)
                        .sort()
                        .map((type: string) => (
                          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#a0aec0', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={!!mapFilters.enabledResourceNodeTypes[type]}
                              onChange={() => toggleResourceNodeType(type)}
                              style={{ cursor: 'pointer' }}
                            />
                            {type}
                          </label>
                        ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Faction Warfare */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Faction Warfare</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => callEndpoint('/api/admin/prod/faction-warfare/seed', 'Seed Territories', undefined, 'POST')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Seed Initial Territories
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/faction-warfare/force-tick', 'Force Tick', undefined, 'POST')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Force Warfare Tick
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/faction-warfare/stats', 'Get Stats', undefined, 'GET')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Get Faction Stats
              </button>
            </div>
          </div>

          {/* Server Info */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Server Info</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => callEndpoint('/api/admin/prod/stats', 'Fetch Stats', undefined, 'GET')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Refresh Stats
              </button>
              <button
                onClick={() => callEndpoint('/api/world/config', 'World Config', undefined, 'GET')}
                disabled={state.isBusy}
                style={buttonStyle(state.isBusy)}
              >
                Get World Config
              </button>
            </div>

            {serverStats && (
              <div style={{ marginTop: 16, backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 12, color: '#ffa500', marginBottom: 10, textTransform: 'uppercase' }}>
                  Live Server Snapshot
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                  <div style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Online Players</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>{serverStats.connectedPlayers}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Ships Active</span>
                    <span style={{ color: '#3b82f6', fontWeight: 600 }}>{serverStats.totalShips}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <span>World Seed</span>
                    <span style={{ color: '#f59e0b', fontFamily: 'monospace' }}>{serverStats.worldSeed}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Uptime</span>
                    <span style={{ color: '#a855f7' }}>{serverStats.uptime}</span>
                  </div>
                </div>
              </div>
            )}

            {economyStats && (
              <div style={{ marginTop: 16, backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 12, color: '#ffa500', marginBottom: 10, textTransform: 'uppercase' }}>
                  Economy Snapshot
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
                  <StatLine label="Player Pieces" value={economyStats.currency.totalPlayerPieces} color="#22c55e" />
                  <StatLine label="NPC Pieces" value={economyStats.currency.totalNpcPieces} color="#3b82f6" />
                  <StatLine label="Total Bounties" value={economyStats.currency.totalBounties} color="#ef4444" />
                  <StatLine label="In Circulation" value={economyStats.currency.totalInCirculation} color="#f59e0b" />
                  <StatLine label="Players Online" value={economyStats.players.online} color="#22c55e" />
                  <StatLine label="Avg Player Level" value={economyStats.players.avgLevel.toFixed(1)} color="#a855f7" />
                  <StatLine label="Total Items" value={economyStats.inventory.totalItems} color="#3b82f6" />
                  <StatLine label="Unique Item Types" value={economyStats.inventory.uniqueItemTypes} color="#f97316" />
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Quick Actions</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => {
                  void callEndpoint('/api/admin/prod/reload-collisions', 'Step 1/3');
                  setTimeout(() => callEndpoint('/api/admin/prod/reseed-permanent', 'Step 2/3'), 1000);
                  setTimeout(() => callEndpoint('/api/admin/prod/reset-resources', 'Step 3/3'), 2000);
                }}
                disabled={state.isBusy}
                style={{
                  ...buttonStyle(state.isBusy),
                  backgroundColor: '#553c9a',
                  borderColor: '#6b46c1',
                }}
              >
                Refresh All (Collisions + Permanents + Resources)
              </button>
              <button
                onClick={() => callEndpoint('/api/admin/prod/clear-caches', 'Clear All Client Caches')}
                disabled={state.isBusy}
                style={{
                  ...buttonStyle(state.isBusy),
                  backgroundColor: '#1f2937',
                  borderColor: '#374151',
                }}
              >
                Clear All Client Caches
              </button>
            </div>
          </div>

          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 12 }}>World History</h2>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              {([
                { id: 'online', label: 'Online' },
                { id: 'legends', label: 'Legends' },
                { id: 'recent', label: 'Recent' },
              ] as Array<{ id: 'online' | 'legends' | 'recent'; label: string }>).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setHistoryTab(t.id)}
                  disabled={state.isBusy}
                  style={{
                    ...buttonStyle(state.isBusy),
                    textAlign: 'center',
                    backgroundColor: historyTab === t.id ? '#553c9a' : (state.isBusy ? '#1f2937' : '#2d3748'),
                    borderColor: historyTab === t.id ? '#6b46c1' : '#4a5568',
                    flex: 1,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ color: '#a0aec0', fontSize: 11 }}>Showing: {historyTab}</div>
              <button
                type="button"
                onClick={() => void fetchWorldHistory(adminKey, historyTab)}
                disabled={state.isBusy}
                style={{
                  padding: '6px 10px',
                  backgroundColor: state.isBusy ? '#1f2937' : '#2d3748',
                  color: state.isBusy ? '#718096' : '#e2e8f0',
                  border: '1px solid #4a5568',
                  fontSize: 11,
                  cursor: state.isBusy ? 'default' : 'pointer',
                }}
              >
                Refresh
              </button>
            </div>
            <div style={{ maxHeight: 240, overflow: 'auto', border: '1px solid #4a5568' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.5fr 0.8fr 0.8fr 0.5fr', gap: 8, padding: '8px 10px', backgroundColor: '#2d3748', color: '#a0aec0', fontSize: 10 }}>
                <div>Name</div>
                <div>Lv</div>
                <div>Pieces</div>
                <div>Infamy</div>
                <div>Status</div>
              </div>
              {worldHistory.length === 0 && (
                <div style={{ padding: 10, color: '#718096', fontSize: 11 }}>No data.</div>
              )}
              {worldHistory.map((c) => (
                <div key={`${c.id}_${c.isOnline ? 'on' : 'off'}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.5fr 0.8fr 0.8fr 0.5fr', gap: 8, padding: '8px 10px', borderTop: '1px solid #4a5568', backgroundColor: '#1a1a2e', color: '#e2e8f0', fontSize: 11 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  <div>{c.level}</div>
                  <div>₱{Number(c.berries || 0).toLocaleString()}</div>
                  <div>{Number(c.infamy || 0).toLocaleString()}</div>
                  <div style={{ color: c.isOnline ? '#00ff88' : '#a0aec0' }}>{c.isOnline ? 'ON' : 'OFF'}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
            <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16 }}>Player Ops</h2>
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 6 }}>Account search (email / username / display)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={accountSearchQuery}
                  onChange={(e) => setAccountSearchQuery(e.target.value)}
                  placeholder="Search accounts..."
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => void doAccountSearch()}
                  disabled={state.isBusy}
                  style={{ ...buttonStyle(state.isBusy), width: 120, textAlign: 'center' }}
                >
                  Search
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  value={banDuration}
                  onChange={(e) => setBanDuration(e.target.value)}
                  placeholder="ban duration (e.g. 72h, 15m, 100y)"
                  style={inputStyle}
                />
              </div>
              {accountResults.length > 0 && (
                <div style={{ marginTop: 10, border: '1px solid #4a5568', maxHeight: 180, overflow: 'auto' }}>
                  {accountResults.map((a) => (
                    <div key={a.id} style={{ padding: 10, borderTop: '1px solid #4a5568', backgroundColor: '#0d0d14' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.username || a.displayName || a.email}
                          </div>
                          <div style={{ color: '#a0aec0', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email}</div>
                          <div style={{ color: '#718096', fontSize: 10 }}>id: {a.id}</div>
                          <div style={{ color: '#718096', fontSize: 10 }}>status: {a.status}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 140 }}>
                          <button
                            type="button"
                            onClick={() => void banAccount(a.id)}
                            disabled={state.isBusy}
                            style={{ ...buttonStyle(state.isBusy), backgroundColor: '#742a2a', borderColor: '#9b2c2c' }}
                          >
                            Ban
                          </button>
                          <button
                            type="button"
                            onClick={() => void unbanAccount(a.id)}
                            disabled={state.isBusy}
                            style={buttonStyle(state.isBusy)}
                          >
                            Unban
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ paddingTop: 14, borderTop: '1px solid #4a5568' }}>
              <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 6 }}>Character search (name)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={characterSearchQuery}
                  onChange={(e) => setCharacterSearchQuery(e.target.value)}
                  placeholder="Search characters..."
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => void doCharacterSearch()}
                  disabled={state.isBusy}
                  style={{ ...buttonStyle(state.isBusy), width: 120, textAlign: 'center' }}
                >
                  Search
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input
                  value={currencyAmount}
                  onChange={(e) => setCurrencyAmount(e.target.value)}
                  placeholder="currency amount"
                  style={inputStyle}
                />
              </div>
              {characterResults.length > 0 && (
                <div style={{ marginTop: 10, border: '1px solid #4a5568', maxHeight: 320, overflow: 'auto' }}>
                  {characterResults.map((c) => (
                    <div key={c.id} style={{ padding: 10, borderTop: '1px solid #4a5568', backgroundColor: '#0d0d14' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ color: '#e2e8f0', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.name} <span style={{ color: '#718096', fontSize: 11, fontWeight: 400 }}>(Lv.{c.level})</span>
                          </div>
                          <div style={{ color: '#a0aec0', fontSize: 10 }}>₱{c.berries.toLocaleString()} | infamy {c.infamy.toLocaleString()}</div>
                          {c.factionReputation && (
                            <div style={{ color: '#a0aec0', fontSize: 9, marginTop: 4, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
                              <div>Navy: <span style={{ color: '#3b82f6' }}>{c.factionReputation.navy}</span></div>
                              <div>Pirate: <span style={{ color: '#ef4444' }}>{c.factionReputation.pirate}</span></div>
                              <div>Merchant: <span style={{ color: '#f59e0b' }}>{c.factionReputation.merchant}</span></div>
                              {c.factionReputation.smuggler !== undefined && (
                                <div>Smuggler: <span style={{ color: '#9b59b6' }}>{c.factionReputation.smuggler}</span></div>
                              )}
                              {c.factionReputation.scholars !== undefined && (
                                <div>Scholars: <span style={{ color: '#3498db' }}>{c.factionReputation.scholars}</span></div>
                              )}
                              {c.factionReputation.inquisition !== undefined && (
                                <div>Inquisition: <span style={{ color: '#c0392b' }}>{c.factionReputation.inquisition}</span></div>
                              )}
                            </div>
                          )}
                          <div style={{ color: '#718096', fontSize: 9, marginTop: 4 }}>characterId: {c.id}</div>
                          <div style={{ color: '#718096', fontSize: 9 }}>accountId: {c.accountId}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 140 }}>
                          <button
                            type="button"
                            onClick={() => void setOrAddBerries(c.id, 'add')}
                            disabled={state.isBusy}
                            style={{ ...buttonStyle(state.isBusy), backgroundColor: '#22543d', borderColor: '#276749' }}
                          >
                            Add ₱
                          </button>
                          <button
                            type="button"
                            onClick={() => void setOrAddBerries(c.id, 'set')}
                            disabled={state.isBusy}
                            style={{ ...buttonStyle(state.isBusy), backgroundColor: '#1f2937', borderColor: '#4a5568' }}
                          >
                            Set ₱
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Live Players Management */}
          <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20, gridColumn: 'span 2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ color: '#ffa500', fontSize: 16, margin: 0 }}>Live Online Players</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => setLivePlayersOpen(!livePlayersOpen)} style={{ ...buttonStyle(false), backgroundColor: livePlayersOpen ? '#553c9a' : '#2d3748' }}>
                  {livePlayersOpen ? 'Hide' : 'Show'}
                </button>
                <button type="button" onClick={() => void fetchLivePlayers()} disabled={state.isBusy} style={buttonStyle(state.isBusy)}>
                  Refresh
                </button>
              </div>
            </div>
            {livePlayersOpen && (
              <div>
                {livePlayers.length === 0 ? (
                  <div style={{ color: '#718096', padding: 16, textAlign: 'center' }}>No players online. Click Refresh to load.</div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                      <input
                        value={livePlayerFilter}
                        onChange={(e) => setLivePlayerFilter(e.target.value)}
                        placeholder="Filter players..."
                        style={{ ...inputStyle, maxWidth: 360 }}
                      />
                      <select
                        value={selectedLivePlayer}
                        onChange={(e) => {
                          const nextId: string = e.target.value;
                          setSelectedLivePlayer(nextId);
                          const nextPlayer = livePlayers.find((p) => p.id === nextId);
                          if (nextPlayer) {
                            setLivePiecesAmount(String(nextPlayer.pieces));
                            setLiveLevelAmount(String(nextPlayer.level));
                          }
                        }}
                        style={{ ...inputStyle, maxWidth: 420 }}
                      >
                        <option value="">Select player...</option>
                        {livePlayers
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} (Lv.{p.level})
                            </option>
                          ))}
                      </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 12, alignItems: 'start' }}>
                      <div style={{ backgroundColor: '#0d0d14', border: '1px solid #4a5568', padding: 12 }}>
                        <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 8 }}>Players</div>
                        <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {livePlayers
                            .filter((p) => {
                              const q: string = livePlayerFilter.trim().toLowerCase();
                              if (!q) return true;
                              return (
                                p.name.toLowerCase().includes(q) ||
                                p.shipType.toLowerCase().includes(q) ||
                                p.id.toLowerCase().includes(q)
                              );
                            })
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((p) => (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => {
                                  setSelectedLivePlayer(p.id);
                                  setLivePiecesAmount(String(p.pieces));
                                  setLiveLevelAmount(String(p.level));
                                }}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '10px 10px',
                                  border: p.id === selectedLivePlayer ? '2px solid #ffa500' : '1px solid #2a2a3e',
                                  backgroundColor: p.id === selectedLivePlayer ? '#111827' : '#0a0a0f',
                                  color: '#e2e8f0',
                                  cursor: 'pointer',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                                    <div style={{ fontSize: 10, color: '#a0aec0' }}>Lv.{p.level} | {p.shipType}</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, color: '#ffd700' }}>₱{p.pieces.toLocaleString()}</div>
                                    <div style={{ fontSize: 10, color: '#00ff88' }}>{p.hp}</div>
                                  </div>
                                </div>
                              </button>
                            ))}
                        </div>
                      </div>

                      <div style={{ backgroundColor: '#0d0d14', border: '1px solid #4a5568', padding: 12 }}>
                        {(() => {
                          const selectedPlayer = livePlayers.find((p) => p.id === selectedLivePlayer) || null;
                          if (!selectedPlayer) {
                            return (
                              <div style={{ color: '#718096', fontSize: 12, padding: 16, textAlign: 'center' }}>
                                Select a player to manage.
                              </div>
                            );
                          }

                          return (
                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
                                <div>
                                  <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 700 }}>{selectedPlayer.name}</div>
                                  <div style={{ color: '#a0aec0', fontSize: 11 }}>playerId: {selectedPlayer.id}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ color: '#ffd700', fontSize: 13 }}>₱{selectedPlayer.pieces.toLocaleString()}</div>
                                  <div style={{ color: '#00ff88', fontSize: 11 }}>{selectedPlayer.hp}</div>
                                </div>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                                <div style={{ border: '1px solid #2a2a3e', padding: 12 }}>
                                  <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 8 }}>Give Item</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px', gap: 8, alignItems: 'center' }}>
                                    <select
                                      value={liveGiveItemId}
                                      onChange={(e) => setLiveGiveItemId(e.target.value)}
                                      style={inputStyle}
                                    >
                                      <option value="">Select item...</option>
                                      {liveItems.map((item) => (
                                        <option key={item.id} value={item.id}>
                                          {item.type} · {item.name}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="number"
                                      value={liveGiveQty}
                                      onChange={(e) => setLiveGiveQty(e.target.value)}
                                      min="1"
                                      style={inputStyle}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (!liveGiveItemId) return;
                                        void liveAction(selectedPlayer.id, 'give-item', {
                                          itemId: liveGiveItemId,
                                          quantity: parseInt(liveGiveQty, 10) || 1,
                                        });
                                      }}
                                      disabled={state.isBusy || !liveGiveItemId}
                                      style={{
                                        ...buttonStyle(state.isBusy || !liveGiveItemId),
                                        backgroundColor: '#22543d',
                                        borderColor: '#276749',
                                        textAlign: 'center',
                                      }}
                                    >
                                      Give
                                    </button>
                                  </div>
                                </div>

                                <div style={{ border: '1px solid #2a2a3e', padding: 12 }}>
                                  <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 8 }}>Economy & Level</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 1fr 140px', gap: 8, alignItems: 'center' }}>
                                    <input
                                      type="number"
                                      value={livePiecesAmount}
                                      onChange={(e) => setLivePiecesAmount(e.target.value)}
                                      placeholder="Pieces"
                                      style={inputStyle}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => void liveAction(selectedPlayer.id, 'set-pieces', { amount: parseInt(livePiecesAmount, 10) || 0 })}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), textAlign: 'center' }}
                                    >
                                      Set Pieces
                                    </button>
                                    <input
                                      type="number"
                                      value={liveLevelAmount}
                                      onChange={(e) => setLiveLevelAmount(e.target.value)}
                                      placeholder="Level"
                                      min="1"
                                      max="100"
                                      style={inputStyle}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => void liveAction(selectedPlayer.id, 'set-level', { level: parseInt(liveLevelAmount, 10) || 1 })}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), textAlign: 'center' }}
                                    >
                                      Set Level
                                    </button>
                                  </div>
                                </div>

                                <div style={{ border: '1px solid #2a2a3e', padding: 12 }}>
                                  <div style={{ color: '#a0aec0', fontSize: 11, marginBottom: 8 }}>Actions</div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                                    <button
                                      type="button"
                                      onClick={() => void liveAction(selectedPlayer.id, 'heal')}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), backgroundColor: '#22543d', borderColor: '#276749', textAlign: 'center' }}
                                    >
                                      Heal
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        void (async () => {
                                          if (!(await confirm({ title: 'Kill Player', message: 'Kill?', confirmText: 'Kill', cancelText: 'Cancel', tone: 'danger' }))) return;
                                          void liveAction(selectedPlayer.id, 'kill');
                                        })();
                                      }}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), backgroundColor: '#742a2a', borderColor: '#9b2c2c', textAlign: 'center' }}
                                    >
                                      Kill
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        void (async () => {
                                          if (!(await confirm({ title: 'Clear Inventory', message: 'Clear inventory?', confirmText: 'Clear', cancelText: 'Cancel', tone: 'danger' }))) return;
                                          void liveAction(selectedPlayer.id, 'clear-inventory');
                                        })();
                                      }}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), textAlign: 'center' }}
                                    >
                                      Clear Inv
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        void (async () => {
                                          if (!(await confirm({ title: 'Kick Player', message: 'Kick?', confirmText: 'Kick', cancelText: 'Cancel', tone: 'danger' }))) return;
                                          void liveAction(selectedPlayer.id, 'kick', { reason: 'Admin' });
                                        })();
                                      }}
                                      disabled={state.isBusy}
                                      style={{ ...buttonStyle(state.isBusy), backgroundColor: '#742a2a', borderColor: '#9b2c2c', textAlign: 'center' }}
                                    >
                                      Kick
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Economy Dashboard - EVE Online Style */}
        {economyStats && (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ color: '#ffa500', fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              Economy Dashboard
              <span style={{ fontSize: 10, color: '#718096', fontWeight: 'normal' }}>[Auto-refresh: 10s]</span>
            </h2>

            {/* Currency Ticker Strip */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <TickerValue label="Total Players" value={economyStats.currency.totalPlayerPieces} prefix="₱" color="#ffd700" />
              <TickerValue label="Avg Per Player" value={economyStats.currency.avgPlayerPieces} prefix="₱" />
              <TickerValue label="Total Bounties" value={economyStats.currency.totalBounties} prefix="₱" color="#ef4444" />
              <TickerValue label="NPC Wealth" value={economyStats.currency.totalNpcPieces} prefix="₱" color="#3b82f6" />
              <TickerValue label="In Circulation" value={economyStats.currency.totalInCirculation} prefix="₱" color="#00ff88" />
            </div>

            {/* Sparkline for currency history */}
            {economyHistory.length > 1 && (
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16, marginBottom: 20 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 10, color: '#718096', marginBottom: 8, textTransform: 'uppercase' }}>
                  Player Wealth Trend (Last {economyHistory.length} samples)
                </div>
                <SparklineChart data={economyHistory.map(h => h.value)} width={300} height={40} color="#ffd700" />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
              {/* Wealth Leaderboard */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  Top 10 Wealthiest Players
                </div>
                <HorizontalBarChart data={economyStats.players.wealthRanking.map(p => ({ label: p.name, value: p.pieces, color: '#ffd700' }))} />
              </div>

              {/* Item Distribution */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  Top Items in Circulation
                </div>
                <HorizontalBarChart data={economyStats.inventory.topItems.map(item => ({ label: item.name, value: item.count, color: '#00ff88' }))} />
                <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #2a2a3e', display: 'flex', justifyContent: 'space-between', fontFamily: '"Sometype Mono", monospace', fontSize: 10, color: '#718096' }}>
                  <span>Total Items: {economyStats.inventory.totalItems.toLocaleString()}</span>
                  <span>Types: {economyStats.inventory.uniqueItemTypes}</span>
                </div>
              </div>

              {/* NPC Faction Distribution */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  NPC Faction Distribution ({economyStats.npcs.total} total)
                </div>
                <FactionBar data={economyStats.npcs.byFaction} />
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 10, color: '#718096', marginBottom: 8 }}>Player Level Distribution</div>
                  <HorizontalBarChart data={Object.entries(economyStats.players.levelDistribution).map(([bracket, count]) => ({ label: `Lv ${bracket}`, value: count, color: '#8b5cf6' }))} />
                </div>
              </div>
            </div>

            {/* Summary Stats Row */}
            <div style={{ display: 'flex', gap: 16, marginTop: 16, padding: 12, backgroundColor: '#0d0d14', border: '1px solid #2a2a3e' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase' }}>Players Online</div>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 20, color: '#00ff88', fontWeight: 600 }}>{economyStats.players.online}</div>
              </div>
              <div style={{ width: 1, backgroundColor: '#2a2a3e' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase' }}>Avg Level</div>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 20, color: '#8b5cf6', fontWeight: 600 }}>{economyStats.players.avgLevel}</div>
              </div>
              <div style={{ width: 1, backgroundColor: '#2a2a3e' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase' }}>Total NPCs</div>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 20, color: '#3b82f6', fontWeight: 600 }}>{economyStats.npcs.total}</div>
              </div>
              <div style={{ width: 1, backgroundColor: '#2a2a3e' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase' }}>Inventories</div>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 20, color: '#f59e0b', fontWeight: 600 }}>{economyStats.inventory.totalPlayers}</div>
              </div>
            </div>
          </div>
        )}

        {/* Market Bot Dashboard */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ color: '#ffa500', fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              Market Bots (Economy Simulation)
            </h2>
            <button
              type="button"
              onClick={() => setMarketBotPanelOpen(!marketBotPanelOpen)}
              style={{ ...buttonStyle(false), backgroundColor: marketBotPanelOpen ? '#553c9a' : '#2d3748' }}
            >
              {marketBotPanelOpen ? 'Hide' : 'Show'}
            </button>
          </div>

          {marketBotPanelOpen && (
            <MarketBotAdminPanel apiBaseUrl={apiBaseUrl} adminKey={adminKey} />
          )}
        </div>

        {/* Faction & NPC Dashboard */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ color: '#ffa500', fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              Faction & NPC Dashboard
            </h2>
            <button
              type="button"
              onClick={() => {
                setFactionPanelOpen(!factionPanelOpen);
                if (!factionPanelOpen) {
                  void fetchFactionStats(adminKey);
                }
              }}
              style={{ ...buttonStyle(false), backgroundColor: factionPanelOpen ? '#553c9a' : '#2d3748' }}
            >
              {factionPanelOpen ? 'Hide' : 'Show'}
            </button>
          </div>

          {factionPanelOpen && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {/* Faction Power Rankings */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  Faction Power Rankings
                </div>
                {factionStats?.factions ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {factionStats.factions.map((f) => (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 100, fontSize: 11, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                        <div style={{ flex: 1, height: 8, backgroundColor: '#1a1a2e', overflow: 'hidden' }}>
                          <div style={{ width: `${(f.power / 1000) * 100}%`, height: '100%', backgroundColor: f.id === 'navy' ? '#3b82f6' : f.id.includes('pirate') ? '#ef4444' : '#f59e0b' }} />
                        </div>
                        <div style={{ width: 40, fontSize: 10, color: '#718096', textAlign: 'right' }}>{f.power}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: 11 }}>Loading...</div>
                )}
                {factionStats?.territories && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a3e', display: 'flex', gap: 16 }}>
                    <div style={{ fontSize: 10, color: '#718096' }}>Territories: <span style={{ color: '#e2e8f0' }}>{factionStats.territories.total}</span></div>
                    <div style={{ fontSize: 10, color: '#718096' }}>Contested: <span style={{ color: '#ef4444' }}>{factionStats.territories.contested}</span></div>
                    <div style={{ fontSize: 10, color: '#718096' }}>Conflicts: <span style={{ color: '#f59e0b' }}>{factionStats.conflicts?.active || 0}</span></div>
                  </div>
                )}
              </div>

              {/* Ambient NPC Stats */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  Ambient NPCs ({npcStats?.total || 0} total)
                </div>
                {npcStats?.byCategory ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {Object.entries(npcStats.byCategory).map(([cat, count]) => (
                      <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: '#1a1a2e' }}>
                        <span style={{ fontSize: 10, color: '#a0aec0', textTransform: 'capitalize' }}>{cat}</span>
                        <span style={{ fontSize: 10, color: '#e2e8f0', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: 11 }}>Loading...</div>
                )}
                {npcStats?.tradeRoutes && npcStats.tradeRoutes.length > 0 && (
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #2a2a3e' }}>
                    <div style={{ fontSize: 10, color: '#718096', marginBottom: 8 }}>Trade Routes ({npcStats.routes})</div>
                    {npcStats.tradeRoutes.slice(0, 3).map((r) => (
                      <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '2px 0' }}>
                        <span style={{ color: '#a0aec0' }}>{r.name}</span>
                        <span style={{ color: r.dangerLevel > 0.5 ? '#ef4444' : '#22c55e' }}>{(r.dangerLevel * 100).toFixed(0)}% danger</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Faction Events */}
              <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16, gridColumn: 'span 2' }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                  Recent Faction Events
                </div>
                {factionStats?.recentEvents && factionStats.recentEvents.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 150, overflowY: 'auto' }}>
                    {factionStats.recentEvents.map((e) => (
                      <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '4px 8px', backgroundColor: '#1a1a2e', borderLeft: `3px solid ${e.impact === 'major' ? '#ef4444' : e.impact === 'moderate' ? '#f59e0b' : '#3b82f6'}` }}>
                        <span style={{ fontSize: 9, color: '#718096', minWidth: 60 }}>{new Date(e.timestamp).toLocaleTimeString()}</span>
                        <span style={{ fontSize: 10, color: '#e2e8f0' }}>{e.description}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#718096', fontSize: 11, textAlign: 'center', padding: 16 }}>No recent events</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Guest Session History Dashboard */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ color: '#ffa500', fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              👤 Guest Session History
            </h2>
            <button
              type="button"
              onClick={() => {
                setGuestPanelOpen(!guestPanelOpen);
                if (!guestPanelOpen) {
                  void fetchGuestStats(adminKey);
                }
              }}
              style={{ ...buttonStyle(false), backgroundColor: guestPanelOpen ? '#553c9a' : '#2d3748' }}
            >
              {guestPanelOpen ? 'Hide' : 'Show'}
            </button>
          </div>

          {guestPanelOpen && (
            <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', textTransform: 'uppercase' }}>
                  Anonymous Player Sessions
                </div>
                <button
                  type="button"
                  onClick={() => void fetchGuestStats(adminKey)}
                  disabled={state.isBusy}
                  style={{
                    padding: '4px 8px',
                    fontSize: 10,
                    backgroundColor: state.isBusy ? '#1f2937' : '#2d3748',
                    color: state.isBusy ? '#718096' : '#e2e8f0',
                    border: '1px solid #4a5568',
                    cursor: state.isBusy ? 'default' : 'pointer',
                  }}
                >
                  Refresh
                </button>
              </div>

              {guestStats ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                  <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#718096', marginBottom: 4 }}>Window (Days)</div>
                    <div style={{ fontSize: 18, color: '#00ff88', fontWeight: 600 }}>{guestStats.windowDays}</div>
                  </div>
                  <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#718096', marginBottom: 4 }}>Unique Guests</div>
                    <div style={{ fontSize: 18, color: '#3b82f6', fontWeight: 600 }}>{guestStats.totalUniqueGuests}</div>
                  </div>
                  <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#718096', marginBottom: 4 }}>Guest Sessions</div>
                    <div style={{ fontSize: 18, color: '#f59e0b', fontWeight: 600 }}>{guestStats.totalGuestSessions}</div>
                  </div>
                  <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#718096', marginBottom: 4 }}>Avg Sessions/Guest</div>
                    <div style={{ fontSize: 18, color: '#a855f7', fontWeight: 600 }}>
                      {guestStats.totalUniqueGuests > 0 ? (guestStats.totalGuestSessions / guestStats.totalUniqueGuests).toFixed(1) : '0'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#718096', fontSize: 11, padding: 16, textAlign: 'center' }}>Loading...</div>
              )}

              {guestStats && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 10, color: '#a0aec0', marginBottom: 8, textTransform: 'uppercase' }}>
                    Daily Guest Metrics
                  </div>
                  {guestStats.daily.length > 0 ? (
                    <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #2a2a3e' }}>
                      {guestStats.daily.slice().reverse().map((row) => (
                        <div key={row.date} style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a3e', backgroundColor: '#1a1a2e', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#e2e8f0' }}>
                          <span style={{ color: '#a0aec0' }}>{row.date}</span>
                          <span>Unique: <strong style={{ color: '#3b82f6' }}>{row.uniqueGuests}</strong></span>
                          <span>Sessions: <strong style={{ color: '#f59e0b' }}>{row.sessions}</strong></span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#718096', fontSize: 11, padding: 16, textAlign: 'center', backgroundColor: '#1a1a2e' }}>
                      No guest sessions in selected window
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Rift Management Dashboard */}
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ color: '#ffa500', fontSize: 18, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              🌊 Uncharted Waters - Rift System
            </h2>
            <button
              type="button"
              onClick={() => setRiftPanelOpen(!riftPanelOpen)}
              style={{ ...buttonStyle(false), backgroundColor: riftPanelOpen ? '#553c9a' : '#2d3748' }}
            >
              {riftPanelOpen ? 'Hide' : 'Show'}
            </button>
          </div>

          {riftPanelOpen && (
            <RiftManagementPanel apiBaseUrl={apiBaseUrl} adminKey={adminKey} />
          )}
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: '1px solid #4a5568',
          color: '#718096',
          fontSize: 12,
          textAlign: 'center',
          fontFamily: 'var(--font-lookout)',
        }}>
          Grand Line Production Admin Panel
        </div>
      </div>
    </div>
  );
};

const buttonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 12px',
  backgroundColor: disabled ? '#1f2937' : '#2d3748',
  color: disabled ? '#718096' : '#e2e8f0',
  border: '1px solid #4a5568',
  fontSize: 12,
  cursor: disabled ? 'default' : 'pointer',
  textAlign: 'left',
});

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#2d3748',
  border: '1px solid #4a5568',
  color: '#e2e8f0',
  fontSize: 12,
  width: '100%',
};

export default ProductionAdminPanel;
